import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ordersService, {
  Order,
  OrderDetails,
  OrderLeg,
  StreetTurnOpportunity,
  buildInvoiceHeaderPatch,
  buildInvoiceDataRows,
} from '../services/orders.service';
import {
  invoiceLineItemsService,
  type InvoiceLineItemBatchItem,
} from '../services/invoiceLineItems.service';
import proDocumentsService from '../services/proDocuments.service';
import type { QuoteLookupContext } from '../services/quote.service';
import { invoiceEstimateService, isMainInvoiceLine, type EstimatedLine } from '../services/invoiceReconcile.service';
import templateService from '../services/template.service';
import { isCreateSentinel } from '../components/pro/createSentinels';
import { useOrderViewers } from './useOrderViewers';
import { detectChangedFields, getDispatchScope, getLegsLeavingScopeOnDrop } from '../utils/orderLegDiff';
import { streetTurnLegBlock } from '../utils/streetTurnLegBlock';
import { computeDriverArrivalDateTime } from '../utils/driverArrivalAutofill';
import { buildLegSavePayload } from '../utils/legSavePayload';
import { computeAllScopeApptFills, computeScopeApptFills } from '../utils/scopeDateAutofill';
import { buildOrderDataRows } from '../utils/buildOrderDataRows';
import {
  createOrderValidationErrors,
  createOrderWarnings,
  deliveryOrderDocError,
} from '../utils/createOrderValidation';
import { validateContainerRequired } from '../utils/containerValidator';
import { importWeightRequiredMessage, isImportMissingWeight } from '../utils/importWeightRule';
import { validateChassisCompatibility } from '../utils/chassisValidation';
import { useChassisCompatibility } from './useChassisCompatibility';
import { validateLegChanges } from '../utils/legValidation';
import { planDeliveryDemotion } from '../utils/legDeliveryDemotion';
import { validateOrderSave, hazmatInfoLegActive } from '../pages/pro/components/OrderFormView';
import { whseGlCodeErrors } from '../pages/pro/components/invoiceLineItemShared';
import { isTemplateApproved, templateFieldEditable } from '../pages/pro/templateFieldPolicy';
import {
  DEST_MERGE_FIELDS,
  DEST_SIDE_FIELDS,
  buildDestSideFromDest,
  buildDestSideFromOrig,
  buildOrigSideFromDest,
  carryDispatchAcrossSplit,
  cascadeLegDropChange,
  clearDestSide,
  overlayFromLeg,
  planLiveDriverInherit,
} from '../utils/orderLegInheritance';

/**
 * Map of stale-data conflicts surfaced by the most recent `save()` attempt.
 *
 * Keys are stable strings:
 *   - Order-level field: the camelCase field name (e.g. `chassisType`).
 *   - Leg-level field:   `leg:{legrecno}:{field}` (e.g. `leg:9123:driver`).
 *   - Whole-leg structural change: `leg:{legrecno}:__new__` or
 *     `leg:{legrecno}:__removed__`.
 *
 * `theirs` is the freshly-fetched server value; `mine` is the user's edit
 * (or `undefined` for whole-leg structural changes the hook surfaces but
 * does not itself produce edits for).
 */
export type ConflictMap = Record<string, { theirs: unknown; mine: unknown }>;

/** A post-save message for the user — v1's post-save alert() (native mode's
 * `userMessages`: dupe/seal warnings, etc.). Surfaced as a toast by level. */
export type SaveUserMessage = { text: string; level: 'error' | 'warning' | 'info' };

export type SaveResult =
  | {
      ok: true;
      freshOrderDetails: OrderDetails;
      userMessages?: SaveUserMessage[];
      // No `conflict` here on purpose: a leg conflict blocks the write and comes back as
      // 'stale', so an ok result is always a save that fully landed. It used to be
      // possible for a save to succeed with its leg section silently dropped.
      /** Create mode only: the pronumber the backend just allocated. The host
       *  uses it to flip the drawer from the 'NEW' sentinel to the real pro. */
      /** number for an order, "T…" string for a template (#33471). */
      createdPronumber?: number | string;
    }
  | { ok: false; type: 'no-changes' }
  | { ok: false; type: 'validation'; errors: string[] }
  | {
      ok: false;
      type: 'stale';
      changedOnSave: Record<number, string[]>;
      freshOrderDetails: OrderDetails;
      removedLegrecnos: number[];
      /** Leg NUMBERS of the legs another user deleted, read off the pre-refresh
       *  snapshot. Those rows are gone from `freshOrderDetails`, so unlike changed or
       *  added legs they cannot carry a per-leg badge — the caller names them in its
       *  "changed elsewhere" message instead. */
      removedLegnums: number[];
      /** Leg NUMBERS that changed or were added, for the same message. */
      changedLegnums: number[];
      conflicts: ConflictMap;
    }
  // Native mode only: the backend found a duplicate container/BOL and the user
  // declined the "save anyway" confirm (or no confirm handler was provided).
  // Nothing was written.
  | { ok: false; type: 'duplicate'; dupes: Array<string | number> }
  | { ok: false; type: 'aborted' }
  | { ok: false; type: 'error'; error: string };

/**
 * State for the drop→appt-date modal, raised when an LC marks a leg drop=Y
 * (from either the grid Drop badge or the order-level isDrop toggle).
 *   - 'confirm': the split-off legs are driver-free — the change is applied and
 *     we offer to clear their appointment dates. `legrecnos` are the legs that
 *     actually carry a date; `legs` is display metadata for the modal list.
 *   - 'blocked': a dispatched leg would be stranded in the split-off run. An LC
 *     can clear neither that driver nor its (locked) dates, so the drop/live
 *     change is PREVENTED and an info modal directs them to dispatch.
 */
export type DropApptClearPrompt =
  | { kind: 'blocked' }
  | {
      kind: 'confirm';
      legrecnos: number[];
      legs: { legrecno: number; legnum: number; label: string }[];
    };

/** Diagnostics captured when the order-details fetch fails with a non-404 —
 * everything support needs to trace the request (see ProDetailContainer's
 * copy-to-clipboard error report). */
export type LoadErrorInfo = {
  /** HTTP status, or null when no response ever arrived (network/gateway drop). */
  status: number | null;
  /** Transport error code when present (e.g. axios ERR_NETWORK, ECONNABORTED). */
  code: string | null;
  message: string;
  /** Backend `detail` string when the response carried one. */
  detail: string | null;
  /** Request URL (path) that failed. */
  url: string | null;
  /** ISO timestamp of the failure, in the user's local clock (UTC ISO). */
  at: string;
};

/** Normalize any thrown fetch error (axios or otherwise) into LoadErrorInfo. */
const toLoadErrorInfo = (error: any): LoadErrorInfo => ({
  status: error?.response?.status ?? null,
  code: error?.code ?? null,
  message: error?.message != null ? String(error.message) : String(error),
  detail:
    typeof error?.response?.data?.detail === 'string'
      ? error.response.data.detail
      : null,
  url: error?.config?.url ?? null,
  at: new Date().toISOString(),
});

export type UseOrderEditing = {
  // data (real)
  order: Order | null;
  orderDetails: OrderDetails | null;
  loading: boolean;
  /**
   * True when the last details fetch failed for a reason other than a real
   * 404, so `orderDetails === null` means "couldn't load", not "doesn't
   * exist". Cleared on the next successful load/refresh.
   */
  loadFailed: boolean;
  /** Diagnostics for that failure (null when loadFailed is false). */
  loadError: LoadErrorInfo | null;
  /**
   * Re-fetches order details. Does NOT refetch streetTurnOpportunities and
   * does NOT toggle `loading` (callers wanting a refresh indicator can track
   * locally). Safe to call on unmounted component (no state writes).
   */
  refresh: () => Promise<void>;
  streetTurnOpportunities: StreetTurnOpportunity[];
  orderViewers: ReturnType<typeof useOrderViewers>['orderViewers'];

  // edits (P1.2)
  editedOrderFields: Record<string, any>;
  editedLegs: Record<number, Partial<OrderLeg>>;
  hasChanges: boolean;
  /** Like `hasChanges` but ignores legs whose only staged edit is the
   * `sendToGwiMove` dispatch flag (which the Default-Send-to-GWI-Move
   * preference auto-stages on load). Used to gate the Reschedule Requested
   * trouble ticket so that flag doesn't trigger a false "unsaved changes". */
  hasChangesIgnoringDispatchFlags: boolean;
  /** Like `hasChanges` but ignores legs whose only staged edit is an
   * *auto-staged* `sendToGwiMove: true` — the value the Default-Send-to-GWI-Move
   * effects stamp on load for a leg that wasn't already sending. A user turning
   * the flag off (or any other edit) still counts. Drives the close/discard +
   * page-unload guards so opening & closing a pro with that preference on
   * doesn't raise a false "unsaved changes" warning. */
  hasChangesIgnoringAutoStagedSend: boolean;
  updateOrderField: (name: string, value: any) => void;
  revertOrderField: (name: string) => void;
  /** Admin Override (#33501): snapshot / measure / undo staged edits. The host takes a
   *  snapshot when override goes on, and restores it if the user discards on the way
   *  out — so only the edits override enabled are undone. */
  snapshotEdits: () => EditSnapshot;
  countEditsSince: (snap: EditSnapshot | null) => number;
  revertToSnapshot: (snap: EditSnapshot | null) => void;
  updateLegField: (legrecno: number, name: string, value: any) => void;
  resetEdits: () => void;
  /**
   * Mirror the Invoicing tab's line-item draft into the hook so it survives the
   * tab unmount and commits on the order-level Save. InvoicingView owns the row
   * shape + CRUD UI and calls this on every change with the current batch items
   * plus whether they differ from the server. The order save (`save`) persists
   * them via the batch endpoint alongside the order + invoice-header write.
   */
  setInvoiceLineItemsDraft: (items: InvoiceLineItemBatchItem[], dirty: boolean) => void;
  /** The current line-item draft (null when untouched / after a save). The
   *  Invoicing tab reads this on (re)mount to restore edits made before a tab
   *  switch; null means "seed from the server line items". */
  invoiceLineItemsDraft: InvoiceLineItemBatchItem[] | null;
  /**
   * Mirror the Docs tab's staged Invoice (forinvoice) flags into the hook, the same way
   * the Invoicing tab mirrors its line items. V1 never wrote this field on its own: the
   * grid ships every dropdown with the order save (dosaverecord's `documentlist` prefix
   * -> processSaveDocumentsInvoicing, lib_editrecord.pl:10762), which is why saving each
   * toggle immediately both diverged from V1 and let a per-click refetch land on top of
   * the next click. Keyed by cms_map.recno; values are the DESIRED flag.
   */
  setDocInvoiceFlagsDraft: (flags: Record<number, 'Y' | 'N'>) => void;
  /** The staged flags (empty when untouched / after a save). The Docs tab overlays these
   *  on the rows it renders so a staged change survives a tab switch. */
  docInvoiceFlagsDraft: Record<number, 'Y' | 'N'>;
  /** Increments after every successful save so tabs can fire post-commit side
   *  effects (e.g. the Invoicing tab's Change-All fan-out). */
  saveVersion: number;

  /**
   * Pending structural leg additions (legrecno < 0). These are full leg
   * objects synthesized from neighbor data — not yet persisted. The save
   * flow forwards them to `buildLegSavePayload` which marks them as new.
   */
  pendingNewLegs: OrderLeg[];
  /**
   * Real legrecnos (positive) that the user has marked for removal. Save
   * forwards them through `buildLegSavePayload`'s `removedLegs` channel.
   */
  pendingRemovedLegrecnos: number[];
  /**
   * Memoized "what the user sees" leg list. Derived from `originalLegs`
   * (filtered by `pendingRemovedLegrecnos`) concatenated with `pendingNewLegs`,
   * sorted by `legnum`, with `editedLegs[legrecno]` overlaid per-field, and
   * `legnum` resequenced 1..N for display.
   */
  effectiveLegs: OrderLeg[];
  /**
   * The open form's quote-match context for the spyglass (V1's quotevars, built
   * from the edited-overlaid order + effective legs). Null until details load.
   * Pass it to quoteService.lookupQuoteForPro so "Find Quote" matches on STAGED
   * values — a rate zone or bill-to just picked — the way V1's did.
   */
  quoteLookupContext: QuoteLookupContext | null;
  /**
   * Raw server-side leg snapshot. Same reference as `orderDetails.legs` but
   * surfaced separately for consumers that only need the loaded values
   * (e.g. comparing edited driver/sendToGwiMove against what's on the server).
   */
  originalLegs: OrderLeg[];
  /**
   * Insert a new leg into the user-visible legs.
   *  - 'first' (position ignored): prepend before the current first leg.
   *  - 'mid' (position is 1-based legnum of the leg to split): split that leg
   *    in place, moving its destination onto the inserted leg.
   *  - 'last' (position ignored): append after the current last leg.
   */
  addLeg: (type: 'first' | 'mid' | 'last', position: number) => void;
  /**
   * Mark a leg for removal. Pending-new legs (negative legrecno) are
   * dropped immediately. Real legs with any recorded arrival/depart
   * datetime on the affected stop are blocked and return `{ ok: false, reason }`.
   *
   * `removeType` selects which physical stop is being removed:
   *   - 'origin' (default): removes the shared stop at this leg's origin.
   *     On an interior leg, the previous leg absorbs this leg's dest-side
   *     fields (location, appt, datetime, memo) so the chain stays connected.
   *     On the first leg, the second leg becomes the new first.
   *   - 'destination': only valid on the last leg. Removes the final
   *     destination stop; the now-last leg keeps its existing destination.
   */
  removeLeg: (
    legrecno: number,
    removeType?: 'origin' | 'destination',
  ) => { ok: boolean; reason?: string };
  /**
   * Undo a prior `removeLeg` for a real (positive) legrecno. No-op if the
   * legrecno isn't currently in `pendingRemovedLegrecnos`.
   */
  restoreLeg: (legrecno: number) => void;
  /**
   * Clear the driver across the dispatch scope of `legrecno`. When
   * `options.clearDates` is true, also null the four arrival/depart datetime
   * fields on every leg in scope. Mirrors the drawer's `executeClearDriver`
   * behavior at OrderDetailsModalContent.tsx:2114-2131.
   */
  clearDriver: (legrecno: number, options?: { clearDates?: boolean }) => void;
  /**
   * Clear ONLY the appt DATES (origApptDate / destApptDate) on the given legs;
   * appt times are left intact. Backs the drop→clear-appt-dates confirm.
   */
  clearLegApptDates: (legrecnos: number[]) => void;
  /**
   * Prompt raised when an LC drops a leg that splits dated legs out of the
   * dispatch grouping. Rendered by ProDetailContainer as a confirm ('confirm')
   * or an info modal ('blocked'). Null when idle.
   */
  dropApptClearPrompt: DropApptClearPrompt | null;
  /** Dismiss the drop→clear-appt-dates prompt without clearing (X / Keep / OK). */
  dismissDropApptClearPrompt: () => void;
  /** Confirm the drop→clear-appt-dates prompt: clear the dates, then dismiss. */
  confirmDropApptClear: () => void;
  /**
   * Escape-hatch setter for callers that need to apply complex multi-field
   * updates atomically (e.g. dispatch-scope-aware merging, location swaps that
   * sync sibling legs). Prefer `updateLegField` for single-field updates.
   */
  _setEditedLegs: React.Dispatch<React.SetStateAction<Record<number, Partial<OrderLeg>>>>;
  /**
   * Escape-hatch setter for callers that need to remove keys from the order
   * fields edits map (e.g. resetting a field back to "no edit" via destructure).
   */
  _setEditedOrderFields: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  /**
   * Escape-hatch setter for callers (notably the drawer when seeded by a
   * parent that owns its own fetch loop) to push fresh OrderDetails into the
   * hook without paying for a redundant network round-trip. Prefer `refresh()`
   * when you don't already have the new data.
   */
  _setOrderDetails: React.Dispatch<React.SetStateAction<OrderDetails | null>>;

  // save (P1.4)
  save: (options?: {
    deadlineNote?: string;
    excludeLegrecnos?: number[];
    deletereason?: string;
    /** Transient prompt inputs the saverules engine reads via ctx.input()
     *  (V1 $qpst->param) — currently the rolling modal's rollingreason /
     *  rollingissue / missedcutoffyesno. Not persisted columns. */
    inputs?: Record<string, string>;
    /** Native mode: called when the backend reports a duplicate container/BOL.
     * Return true to resend with bypassDupeCheck (v1 "Save anyway"). Absent -> the
     * save returns a 'duplicate' result for the caller to handle. */
    confirmDuplicate?: (dupes: Array<string | number>) => Promise<boolean>;
    /** V1 field-9 soft gate: when Pending Approval is set to RESOLVED with a
     * blank Bill To Ref#, prompt "you're missing a Bill to Ref# — continue?".
     * Return false to abort the save. Absent -> proceed without prompting. */
    confirmMissingBillToRef?: () => Promise<boolean>;
    /** Create-only soft gate: blank Quote # and/or no invoice line items.
     * Return false to abort the create. Absent -> proceed without prompting. */
    confirmCreateWarnings?: (warnings: string[]) => Promise<boolean>;
  }) => Promise<SaveResult>;
  isSaving: boolean;
  saveError: string | null;
  /** Imperative setter so consumers can dismiss the inline error banner. */
  clearSaveError: () => void;
  validateChanges: () => string[];
  /** True once a save/create has been blocked by validation. Consumers use it
   *  to decide whether to render the live `validateChanges()` banner — clean on
   *  open, then live for the rest of the editing session. */
  validationAttempted: boolean;
  /**
   * Inject async-computed validation errors that must block save alongside
   * the synchronous validators. Backed by a Map keyed by `source` so
   * multiple independent contributors (driver re-qual from LegsGrid,
   * mode/terminal check from OrderFormView, etc.) can coexist without
   * clobbering each other. validateChanges flattens all values.
   *
   * Pass `[]` for a key to remove its contribution; the cache reflects
   * the latest write synchronously, so the save flow won't race with an
   * in-flight check.
   */
  setExtraSaveErrors: (source: string, errors: string[]) => void;
  /**
   * Stale-data conflicts surfaced by the most recent `save()` attempt. Empty
   * when there are no outstanding conflicts. Cleared by `resetEdits()`,
   * `clearConflicts()`, or by another save that succeeds without conflicts.
   * See {@link ConflictMap} for the key format.
   */
  conflicts: ConflictMap;
  /** Imperative setter to dismiss conflict highlights without resetting edits. */
  clearConflicts: () => void;
  /**
   * Map of leg fields that became stale when `orderDetails` was refreshed
   * externally (someone else edited the order while the user had pending
   * edits). Keyed by legrecno. Only fields the user has pending edits for
   * are tracked — fields without local edits aren't "stale" from the user's
   * perspective. UI uses this to render an amber-ring highlight on affected
   * cells. Cleared on `resetEdits()` or per-field via `clearStaleField`.
   */
  staleFields: Record<number, string[]>;
  /** Remove a single legrecno/field pair from `staleFields` (e.g. after the
   * user interacts with the highlighted cell). */
  clearStaleField: (legrecno: number, field: string) => void;
};

// A pending (unsaved) inserted leg + where it goes relative to the existing legs.
type LegInsertSpec =
  | { type: 'first' }
  | { type: 'last' }
  | { type: 'mid'; afterLegrecno: number };
type PendingNewLegEntry = { leg: OrderLeg; insert: LegInsertSpec };

/** A point-in-time copy of the staged edits, restored by revertToSnapshot to undo exactly
 *  what was staged after it — and nothing the user had staged before. Two callers take one:
 *  Admin Override when it is switched ON (#33501), and the mandatory REJECTED trouble
 *  ticket immediately before it stages the REJECTED (#33683). See useOrderEditing.snapshotEdits. */
export interface EditSnapshot {
  orderFields: Record<string, any>;
  legs: Record<string, Partial<OrderLeg>>;
  newLegs: PendingNewLegEntry[];
  removedLegrecnos: number[];
  /** State of the Admin Override "was used" latch when the snapshot was taken. Restored
   *  rather than blanked on revert — see revertToSnapshot. */
  overrideUsed: boolean;
}

// Compose the visible leg order from the server legs + pending inserts − pending
// removals. Inserts are applied against the FULL original list (so `afterLegrecno`
// anchors resolve) and iterated until no more can be placed — so placement is
// INDEPENDENT of pendingNewLegs order: a leg anchored to a later-inserted pending
// leg (or a chain of them) still lands in its slot rather than being appended to the
// end. Removals are applied LAST, so a leg anchored to a removed leg keeps its
// position (removing first would orphan it). Returns the ordered legs (no editedLegs
// overlay, no legnum resequencing — callers do that).
function composeLegOrder(
  originalLegs: OrderLeg[],
  pendingNewLegs: PendingNewLegEntry[],
  removedSet: Set<number>,
): OrderLeg[] {
  const composed = originalLegs.slice();
  let queue = pendingNewLegs.slice();
  let progressed = true;
  while (queue.length && progressed) {
    progressed = false;
    const deferred: PendingNewLegEntry[] = [];
    for (const entry of queue) {
      const insert = entry.insert;
      if (insert.type === 'first') {
        composed.unshift(entry.leg);
        progressed = true;
      } else if (insert.type === 'last') {
        composed.push(entry.leg);
        progressed = true;
      } else {
        const idx = composed.findIndex(l => l.legrecno === insert.afterLegrecno);
        if (idx === -1) {
          deferred.push(entry); // anchor not placed yet — retry on a later pass
        } else {
          composed.splice(idx + 1, 0, entry.leg);
          progressed = true;
        }
      }
    }
    queue = deferred;
  }
  // Any still unplaceable (anchor genuinely absent) — append so a leg is never dropped.
  for (const entry of queue) composed.push(entry.leg);
  return composed.filter(l => !removedSet.has(l.legrecno));
}

/**
 * Skeleton hook that owns the fetch + street-turn + order-viewers state for an
 * order detail surface. Editing is real as of Task P1.2; save flow ported in
 * Task P1.4 (validate -> stale-pre-check -> POST -> reset -> refresh). UI side
 * effects (toast, modal close, parent refresh, deadline prompt, dispatch
 * removal recording, stale-state mutations) stay in the drawer per P1.5 — the
 * drawer translates SaveResult into those side effects.
 *
 * Optional `seed` lets callers that already have an OrderDetails object (e.g.
 * the OrderDetailsPage hands the drawer a pre-fetched copy via props) skip
 * the initial getOrderDetails fetch on the matching pronumber. Street-turn
 * still loads since it's not seeded.
 */
// True when an appt-date value represents an actual appointment (not the V1
// zero-date sentinels or a blank). Mirrors normalize_appt_date on the backend.
const hasApptDate = (value: unknown): boolean => {
  const s = String(value ?? '').trim();
  return s !== '' && s !== '0' && !s.startsWith('0000-00-00');
};

// A leg is "dispatched" (dispatch-locked) when it carries a dispatch row or a
// dispatched-driver datetime — the same predicate cascadeLegDropChange uses to
// decide it can't silently clear the driver.
const isLegDispatched = (leg: OrderLeg): boolean =>
  Number(leg.dispatchid) !== 0 || String(leg.dspdriverdatetime ?? '').trim() !== '';

// Human label for the drop-clear confirm modal's leg list.
const dropClearLegLabel = (leg: OrderLeg): string => {
  const orig = String(leg.origCityState || leg.origcode || '').trim();
  const dest = String(leg.destCityState || leg.destcode || '').trim();
  return `Leg ${leg.legnum}: ${orig} → ${dest}`;
};

export default function useOrderEditing(
  pronumber: number | string | null,
  options?: {
    seed?: OrderDetails | null;
    /**
     * Read-only Template view (pronumber like "T110"). When true, the hook
     * skips order-only side fetches (street-turn) that would 404 or return
     * junk for a template. Editing/saving stays inert because the host locks
     * the view via <PermissionAccessProvider> and hides the Save button.
     */
    isTemplate?: boolean;
    /**
     * Resolved by `useOrderFormContext().effectiveDispatcherIsGwi`. When this
     * transitions GWI ↔ non-GWI, the hook prunes/restores leg edits.
     */
    effectiveDispatcherIsGwi?: boolean;
    /**
     * User preference from `WorkingStatusPreferenceContext.defaultSendToGwiMove`.
     * Re-applied to all legs on non-GWI → GWI transition.
     */
    defaultSendToGwiMove?: boolean;
    /**
     * Fired when a leg edit transitions a leg's destination Move Type
     * (`legAction2`) from anything to "REJECTED" — either by editing that leg's
     * legAction2 directly, or by editing the next leg's `legtype` (which
     * cascades REJECTED onto this leg's legAction2). The host pops a pre-set
     * "REJECTED" / Driver-Ticket=Y trouble ticket. Port of legacy cms.js
     * leg_movetype / leg_movetype2. `legrecno` is the leg whose legAction2
     * became REJECTED.
     *
     * The ticket is MANDATORY (#33683): the host must pass `revertSnapshot` to
     * `revertToSnapshot` if the modal closes without saving one, which takes the
     * REJECTED — and every cascade it triggered — back off the staged edits. The
     * snapshot is captured before the write, so it is the exact pre-REJECTED state.
     */
    onLegActionRejected?: (info: {
      legrecno: number;
      revertSnapshot: EditSnapshot;
    }) => void;
    /**
     * Fired when typing a stop to DELIVERY auto-demoted the order's previous
     * DELIVERY stop to STOPOFF (#33649). The host raises a toast: the demoted
     * boundary is usually a PAID leg the user can't see change — that lock is
     * exactly what the demotion writes through — so it must not happen silently.
     * `legnums` are display leg numbers, named by the leg whose destination held
     * the DELIVERY. See planDeliveryDemotion.
     */
    onDeliveryDemoted?: (info: { legnums: number[] }) => void;
    /**
     * Whether the current user can edit the hazmat field. Gates the Rule 6
     * hazmat hard-block in `validateOrderSave` (legacy parity: the block only
     * fired for users with write ACL on the hazmat field). Defaults to enforced
     * when unset. Resolved by the /pro page via `useCanEditHazmat`.
     */
    canEditHazmat?: boolean;
    /**
     * Whether the current user can edit the ports fields (Port of Origin /
     * Discharge — the order-lc-fields gate). Gates the import "GTG requires
     * both ports" hard-block below: legacy only enforced it for users who
     * could actually set the ports. Resolved by the /pro page via
     * `useCanEditOrderLcFields`.
     */
    canEditPorts?: boolean;
    /**
     * Whether the current user can edit the invoice-hold pair — the Hold Reason
     * (`ordersall.invoiceholdreason`) and the Revised DO flag
     * (`invoice.requiredreviseddo`). Both ride the SAME `order-lc-fields` grant as
     * the ports fields above (InvoicingView's `holdEditable`), so this resolves
     * from `useCanEditOrderLcFields` too; it is named separately because it gates a
     * different rule and the two could diverge.
     *
     * Gates the "Hold Reason is required when Revised DO is Y" hard-block: a
     * dispatcher (GWI360-Dispatch / DispatchAssistant are View on order-lc-fields)
     * sees both fields read-only, so blocking their save asks them to fix something
     * they cannot reach. Same shape as `canEditPorts` above and the hazmat Rule 6
     * gate in validateOrderSave.
     */
    canEditInvoiceHold?: boolean;
    /**
     * Whether the "Quote # is required." hard-block applies to this user —
     * true when the `invoice-quote-number` component resolves to exactly
     * Edit (2). Exempt (3, also the un-granted default) waives it, so e.g.
     * DispatchManager can edit the field without being forced to fill it.
     * Resolved by ProDetailContainer via `useResolvedAccessLevel`.
     */
    quoteNumberRequired?: boolean;
    /**
     * Whether the current user can edit the weight fields (the
     * `order-weight-fields` grant, via `useCanEditOrderWeightFields`). Does NOT
     * gate the "Weight (Lbs) is required on IMPORT orders" hard-block — that
     * applies to everyone — it only picks the message: a user who can't fix it
     * inline is told to contact the order's LC. Resolved by ProDetailContainer.
     */
    canEditWeightFields?: boolean;
    /**
     * Admin Override Mode (#33501) is ON for this pro right now. Rides every save this
     * hook issues (order/leg, invoice line items) so the write is tagged `override` in
     * cms.history and so the backend lets it past the invoiced locks. Resolved by
     * ProDetailContainer, which owns the mode.
     *
     * Sending it is a REQUEST, not an authorization: the server re-resolves the
     * caller's pro-admin-override grant and 403s a claim without it.
     */
    adminOverrideActive?: boolean;
    /**
     * Whether the current user can edit the per-leg Driver field — resolved
     * from edit access on the `leg-driver-field` component (via
     * `useCanEditDriverField`). Sole gate for the drop→clear-appt-dates prompt:
     * anyone who can edit the driver field can remove a driver assignment
     * themselves without coordinating with a dispatcher, so the prompt fires
     * ONLY for users WITHOUT this access. Resolved by the /pro page.
     */
    canEditDriverField?: boolean;
    /**
     * Create New Order mode (plan §Frontend, design doc §4.1). Set by the
     * create flow when the drawer is opened on the `'NEW'` pronumber sentinel:
     *
     *  - hydration comes from `seed` — no `getOrderDetails`, no street-turn;
     *  - `seed.legs` are held as PENDING-NEW legs (negative legrecnos), so
     *    every existing structural mechanism (composeLegOrder / addLeg /
     *    removeLeg / effectiveLegs / buildLegSavePayload) works unmodified;
     *  - `save()` skips the no-changes early-out and the stale pre-check, runs
     *    the create-only validations, and POSTs one `ordersService.createOrder`
     *    built from `spec` + the staged drawer state.
     *
     * `spec` is the modal's field set — it SEEDS the drawer (finds the template
     * and pre-populates), but the order editor is authoritative at save time:
     * movetype/bol/container are re-read from the current editor state (an edit
     * else the seeded value) when the create payload is built, so the user's
     * changes win over the modal's original pick. `bypassDupeCheck` carries the
     * modal's "Save Order" bypass
     * into the save-time re-check. Create mode deactivates as soon as
     * `pronumber` becomes a real (positive numeric) pro, so flipping the drawer
     * to the created order falls back to the ordinary fetch/save path even if
     * the host leaves `createMode` set.
     */
    createMode?: {
      seed: OrderDetails;
      spec: {
        /** 'order' | 'template' — selects the create endpoint at save time
         *  (#33471). Mirrors CreateOrderSpec on ProDetailSlideOver. */
        kind: 'order' | 'template';
        movetype: string;
        billto: number;
        customercode: number;
        bol: string;
        container: string;
        templatepro?: string;
        bypassDupeCheck: boolean;
      };
      /** Staged notes owned by the create flow (the drawer's NotesTab writes
       *  into flow state, not the hook). Appended to the create payload's
       *  `notes[]` alongside any `save({ deadlineNote })`. */
      notes?: string[];
      /** `documenttype` of every doc staged in this create session — feeds the
       *  Delivery-Order-required gate (see deliveryOrderDocError). */
      stagedDocTypes?: string[];
      /** `documenttype` of the chosen template's docs (copied by reference at
       *  create), or null while that list is unknown — which fails the
       *  Delivery-Order gate OPEN. `[]` when there's no template. */
      templateDocTypes?: string[] | null;
    };
  }
): UseOrderEditing {
  const [order, setOrder] = useState<Order | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // Set when the last details fetch failed for a reason OTHER than a real 404
  // (network drop, 5xx, gateway timeout). Lets the UI say "failed to load"
  // instead of the misleading "not found", with copyable diagnostics
  // (2026-08-25 incident). `loadFailed` in the return derives from this.
  const [loadError, setLoadError] = useState<LoadErrorInfo | null>(null);
  const [streetTurnOpportunities, setStreetTurnOpportunities] = useState<StreetTurnOpportunity[]>(
    []
  );

  // Edit-tracking state (matches drawer's names so drawer can drop its own
  // copies in P1.5).
  const [editedOrderFields, setEditedOrderFields] = useState<Record<string, any>>({});
  const [editedLegs, setEditedLegs] = useState<Record<number, Partial<OrderLeg>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  // True once a save/create was blocked by validation. Gates the form's inline
  // error banner: nothing shows on a freshly-opened record, but after the first
  // blocked attempt the banner re-runs validateChanges on every edit so the
  // list stays live. Reset on a successful save and on resetEdits/reload.
  const [validationAttempted, setValidationAttempted] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<ConflictMap>({});
  const [staleFields, setStaleFields] = useState<Record<number, string[]>>({});
  // Structural leg state — pending adds (full leg objects with legrecno < 0)
  // and pending removes (real legrecnos marked for deletion). Mirrors the
  // drawer's pendingNewLegs / pendingRemovedLegs but stores the synthesized
  // leg data inline so `effectiveLegs` can be derived purely from state.
  //
  // Each entry carries the user's INSERTION INTENT — where in the visible
  // chain the new leg should live — instead of synthesizing a fractional
  // legnum on add. Pure-legnum sorting can't disambiguate multiple sequential
  // inserts off the same parent (they'd all want the same fractional slot);
  // walking the array in insertion order with positional refs avoids that.
  const [pendingNewLegs, setPendingNewLegs] = useState<PendingNewLegEntry[]>([]);
  const [pendingRemovedLegrecnos, setPendingRemovedLegrecnos] = useState<number[]>([]);
  // Mirrors of the four edit-state pieces, so the Admin Override snapshot helpers below
  // can be stable ([] deps) and still read current values. Same pattern the file already
  // uses for effectiveLegsRef.
  const editedOrderFieldsRef = useRef(editedOrderFields);
  editedOrderFieldsRef.current = editedOrderFields;
  const editedLegsRef = useRef(editedLegs);
  editedLegsRef.current = editedLegs;
  const pendingNewLegsRef = useRef(pendingNewLegs);
  pendingNewLegsRef.current = pendingNewLegs;
  const pendingRemovedLegrecnosRef = useRef(pendingRemovedLegrecnos);
  pendingRemovedLegrecnosRef.current = pendingRemovedLegrecnos;
  // Prompt shown to LCs when a drop=Y splits legs (with appt dates) out of the
  // dispatch grouping — offer to clear those dates, or block if a dispatched
  // leg remains. Rendered by ProDetailContainer. Null when idle.
  const [dropApptClearPrompt, setDropApptClearPrompt] = useState<DropApptClearPrompt | null>(null);

  // Invoicing tab draft persistence. The Invoicing tab is only mounted while
  // active, so its line-item draft would be lost on a tab switch — instead the
  // tab mirrors its current batch items + dirty flag up here (see
  // setInvoiceLineItemsDraft). `invoiceDirty` is React state so it flows into
  // hasChanges / SaveBar; the row data lives in a ref (read only at save/
  // validate time, no re-render needed). Invoice HEADER field edits ride the
  // normal editedOrderFields map, so they need no separate store here.
  const [invoiceDirty, setInvoiceDirty] = useState<boolean>(false);
  const invoiceLineItemsRef = useRef<InvoiceLineItemBatchItem[] | null>(null);
  // Docs tab staged Invoice flags. Unlike the line-item draft this lives in STATE rather
  // than a ref: the Docs grid renders from it on every keystroke-free toggle, so it has
  // to re-render. It is small (one char per changed document) so that is cheap.
  const [docInvoiceFlags, setDocInvoiceFlags] = useState<Record<number, 'Y' | 'N'>>({});
  // ...mirrored into a ref because `save` is a useCallback that must read the flags as
  // they are AT SAVE TIME. Reading the state variable there would capture whatever it
  // held when the callback was last built, which is how a late toggle gets dropped.
  const docInvoiceFlagsRef = useRef<Record<number, 'Y' | 'N'>>({});
  // Bumped after every successful save so tabs can fire post-commit side effects
  // (e.g. the Invoicing tab's Change-All fan-out) without owning the save.
  const [saveVersion, setSaveVersion] = useState<number>(0);

  const setInvoiceLineItemsDraft = useCallback(
    (items: InvoiceLineItemBatchItem[], dirty: boolean) => {
      // Hold the draft ONLY while dirty. A non-null invoiceLineItemsDraft is the
      // durable "there are unsaved line edits" signal the Invoicing tab reads to
      // avoid clobbering the draft on a details refresh (see InvoicingView's reset
      // effect). Clean -> null so a post-save/refresh re-seed proceeds normally.
      invoiceLineItemsRef.current = dirty ? items : null;
      setInvoiceDirty((prev) => (prev === dirty ? prev : dirty));
    },
    [],
  );

  const setDocInvoiceFlagsDraft = useCallback((flags: Record<number, 'Y' | 'N'>) => {
    docInvoiceFlagsRef.current = flags;
    setDocInvoiceFlags(flags);
  }, []);

  // Canonical size<->chassis matrix (backend-sourced; bundled default until it
  // loads). Drives the size<->chassis part of validateChassisCompatibility.
  const chassisMatrix = useChassisCompatibility();

  // Clear stale validation errors when the user makes a new edit. Without
  // this, the saveError text under the Save button keeps showing the prior
  // failure (e.g. "found 2 DELIVERY") after the user changes a field — which
  // makes it look like the same error fired again, even when the new state
  // would now fail a different rule (or succeed entirely).
  useEffect(() => {
    setSaveError(null);
  }, [editedLegs, editedOrderFields, pendingNewLegs, pendingRemovedLegrecnos]);

  const { orderViewers } = useOrderViewers({ activeOrderId: order?.pronumber ?? null });

  // Track mount state so async work doesn't write to state after unmount.
  // The setup must explicitly set true: React 18 StrictMode runs effects
  // mount → cleanup → mount, so without re-setting on each mount the ref
  // ends up stuck at false in dev and every `if (mountedRef.current) …`
  // branch (e.g. setIsSaving) silently no-ops.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Abort in-flight save when the hook unmounts so we don't write to state
  // after teardown.
  const saveAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => saveAbortRef.current?.abort(), []);

  // Synchronous re-entrancy lock for save(). `isSaving` (React state) drives the
  // Save button's disabled/spinner UI, but state updates are async — a genuine
  // double-click (or any second submit) can land before the disabled attribute
  // is committed and painted, and both invocations would then run a full save.
  // This ref flips true synchronously before the first await and clears on every
  // exit path, so the second invocation bails immediately. This is the hard
  // guard against a duplicate POST — the root cause of the pro-5053480 legs that
  // got INSERTed twice (two identical cmsapi hits, same client temp recnos).
  const savingRef = useRef<boolean>(false);

  // Seed is read once at fetch decision time only — keep it in a ref so the
  // effect doesn't re-run when the parent passes a new object identity each
  // render. Only the seed's pronumber matters for "should we fetch."
  const seedRef = useRef(options?.seed);
  seedRef.current = options?.seed;

  // Keep the REJECTED-trouble-ticket callback in a ref so updateLegField (which
  // has empty deps) always calls the latest one without changing identity.
  const onLegActionRejectedRef = useRef(options?.onLegActionRejected);
  onLegActionRejectedRef.current = options?.onLegActionRejected;
  // Same reason — updateLegField has empty deps and must reach the latest handler.
  const onDeliveryDemotedRef = useRef(options?.onDeliveryDemoted);
  onDeliveryDemotedRef.current = options?.onDeliveryDemoted;
  // Admin Override (#33501). A REF, not a plain const, for two reasons: `save` is a
  // useCallback whose own `options` parameter shadows the hook's, and a captured value
  // would go stale the moment the user toggled the mode without `save` being rebuilt.
  const adminOverrideActiveRef = useRef(false);
  adminOverrideActiveRef.current = !!options?.adminOverrideActive;
  // LATCH: set the moment an edit is staged while override is on, and NOT cleared when
  // the user toggles override back off — only by a successful save or resetEdits.
  //
  // Without it, turning override off before pressing Save wrote the override edit tagged
  // `user` instead of `override`. Nothing stopped it either: there is no server-side
  // paid-leg guard, so the leg write simply succeeded. The audit trail is the entire
  // reason this feature exists rather than a direct DB edit, so it cannot depend on the
  // user leaving a toggle in the right position at the moment they press Save.
  const adminOverrideUsedRef = useRef(false);
  /** Call from every path that stages an edit. */
  const noteOverrideEdit = useCallback(() => {
    if (adminOverrideActiveRef.current) adminOverrideUsedRef.current = true;
  }, []);

  // Create New Order mode. Held in refs because (a) `save()`'s own parameter is
  // ALSO named `options` (it shadows the hook's), and (b) the host inlines this
  // object every render, so reading it through a ref keeps callback identities
  // stable. `createActive` is the single gate every create branch reads: it goes
  // false the moment the drawer is handed a real pronumber, so the post-create
  // flip to the created order runs the ordinary fetch/reset/save path.
  // Same question ProDetailContainer asks, and it has to be asked the same way.
  // Testing for a positive NUMBER stranded templates in create mode: an allocated
  // template pronumber is a "T…" STRING, so this stayed true after the create and
  // the load effect below kept short-circuiting on "nothing on the server to read"
  // — leaving the drawer showing the SEED (blank masterpro / rate zone / mode, no
  // server-set invoicing flags) and its staged legs still badged New, while the
  // same template opened fresh in another window looked correct (#33471).
  const createActive = !!options?.createMode && isCreateSentinel(pronumber);
  const createModeRef = useRef(options?.createMode);
  createModeRef.current = options?.createMode;
  const createActiveRef = useRef(createActive);
  createActiveRef.current = createActive;

  useEffect(() => {
    if (pronumber == null) return;

    // Create mode: there is nothing on the server to read (the pronumber is the
    // 'NEW' sentinel). The seed hydration lives in the RESET effect below — it
    // is declared after this one, so seeding here would be cleared by it.
    if (createActiveRef.current) {
      setOrder({ pronumber } as unknown as Order);
      setStreetTurnOpportunities([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const seed = seedRef.current;
    if (seed && seed.pronumber === pronumber) {
      // Hydrate from caller's already-loaded OrderDetails — skip the duplicate
      // getOrderDetails fetch, but still load street-turn (not seeded).
      setOrder({ pronumber } as unknown as Order);
      setOrderDetails(seed);
      setLoading(true);
      setStreetTurnOpportunities([]);

      (async () => {
        try {
          // Street-turn is an order-only lookup; skip it for templates.
          if (!options?.isTemplate) {
            const moveType = seed.moveType;
            const stResponse =
              moveType === 'EXPORT' || moveType === 'RAIL TO PORT'
                ? await ordersService.getExportStreetTurn(pronumber)
                : await ordersService.getImportStreetTurn(pronumber);
            if (cancelled) return;
            setStreetTurnOpportunities(stResponse.data);
          }
        } catch {
          // Street turn is optional — don't block.
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    const fetchData = async () => {
      setLoading(true);
      setLoadError(null);
      setStreetTurnOpportunities([]);

      try {
        const response = await ordersService.getOrderDetails(pronumber);
        if (cancelled) return;
        setOrder({ pronumber } as unknown as Order);
        setOrderDetails(response.data);

        // Street-turn is an order-only lookup; skip it for templates.
        if (!options?.isTemplate) {
          const moveType = response.data.moveType;
          try {
            const stResponse =
              moveType === 'EXPORT' || moveType === 'RAIL TO PORT'
                ? await ordersService.getExportStreetTurn(pronumber)
                : await ordersService.getImportStreetTurn(pronumber);
            if (cancelled) return;
            setStreetTurnOpportunities(stResponse.data);
          } catch {
            // Street turn is optional — don't block.
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch order details:', error);
          // A real 404 means the pro genuinely isn't there ("not found");
          // anything else (network, 5xx, gateway) is a load FAILURE.
          setLoadError(
            (error as any)?.response?.status === 404 ? null : toLoadErrorInfo(error)
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // Intentionally only depend on pronumber: seed is read via ref to avoid
    // re-running on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronumber]);

  // Reset edit-tracking state whenever the active order changes. Mirrors the
  // drawer's effect at OrderDetailsModalContent.tsx:340–360. The drawer's
  // defaultSendToGwiMove pre-population is ported separately below (it has to
  // wait for orderDetails to load, so it can't live in this pronumber reset).
  useEffect(() => {
    // Changing order drops the latch with the edits it belonged to — the override mode
    // itself resets on the same boundary (AdminOverrideContext keys on pronumber).
    adminOverrideUsedRef.current = false;
    setEditedOrderFields({});
    setEditedLegs({});
    setPendingNewLegs([]);
    setPendingRemovedLegrecnos([]);
    setSaveError(null);
    setValidationAttempted(false);
    setConflicts({});
    setStaleFields({});
    invoiceLineItemsRef.current = null;
    setInvoiceDirty(false);
    // Keyed save blocks belong to the pro that raised them. They used to be cleared by
    // their own effect cleanups, so they died with the tab; now that a block outlives an
    // unmount (bugfix #33440) one could otherwise ride into the NEXT pro and block a save
    // with nothing on screen to explain it. Safe to clear here: every contributor
    // re-registers off `orderDetails`, which lands after this reset.
    extraSaveErrorsRef.current.clear();

    // Create mode hydrates HERE (not in the load effect above, which runs
    // first and would have its seeding wiped by the resets above). The seed's
    // legs land as PENDING-NEW legs with negative temp legrecnos so every
    // structural mechanism — composeLegOrder, addLeg/removeLeg, effectiveLegs
    // renumbering, buildLegSavePayload's `legrecno < 0 ⇒ new` convention —
    // treats them as new without a single create-mode branch.
    const createMode = createActiveRef.current ? createModeRef.current : null;
    if (!createMode) return;
    const seed = createMode.seed;
    setOrderDetails({ ...seed, legs: [] });
    setPendingNewLegs(
      (seed.legs ?? []).map((leg) => ({
        leg: { ...leg, legrecno: nextTempLegrecno() },
        insert: { type: 'last' as const },
      })),
    );
    // No-template path only: stage a seeded CSR as an edit so it actually rides.
    // `orderData` is a DIFF of the user's edits (buildOrderDataRows emits only
    // fields present in editedOrderFields), and with no template there is no
    // server-side row to supply csr — order_create_service's
    // `_merge_state_for_validation` reads it out of orderData alone, so a CSR the
    // seed pre-filled and the user never touched would 422 "CSR is required."
    // while sitting visibly filled in on the drawer.
    //
    // INERT since 2026-10-16: the blank seed no longer pre-fills a CSR, so there is
    // nothing here to stage and the user's own pick rides as an ordinary edit. Kept
    // because the invariant it encodes — a seeded value must be staged to reach the
    // server — is what makes re-seeding safe if anyone ever does. The template path
    // needs none of it: the server reads csr off the template row.
    if (!createMode.spec.templatepro && seed.csr) {
      setEditedOrderFields({ csr: seed.csr });
    }
    // Stage the seeded line items as the Invoicing tab's draft so they render
    // as pending rows. `dirty` has to be true for the draft to be held (see
    // setInvoiceLineItemsDraft) — which is accurate here: nothing is persisted.
    const seededLineItems = (seed.invoiceLineItems ?? []).map((li) => ({
      recno: null,
      lineNumber: li.lineNumber,
      item: li.item,
      glCode: li.glCode,
      qty: li.qty,
      rate: li.rate,
      rateType: li.rateType,
      description: li.description,
      total: li.total,
    }));
    if (seededLineItems.length > 0) {
      invoiceLineItemsRef.current = seededLineItems;
      setInvoiceDirty(true);
    }
  }, [pronumber]);

  // Detect stale fields: when `orderDetails` changes externally (e.g. another
  // user saved while this user was editing), compare prev vs new orderDetails
  // and mark any field the current user has pending edits for as "stale" so
  // the UI can highlight it. Fields the user hasn't touched aren't stale from
  // their perspective — they just see the latest server value.
  const prevOrderDetailsRef = useRef<OrderDetails | null>(null);
  useEffect(() => {
    const prev = prevOrderDetailsRef.current;
    prevOrderDetailsRef.current = orderDetails;
    if (!prev || !orderDetails) return;
    if (prev === orderDetails) return;
    const changed = detectChangedFields(prev.legs ?? [], orderDetails.legs ?? []);
    const filtered: Record<number, string[]> = {};
    for (const [legrecnoStr, fields] of Object.entries(changed)) {
      const legrecno = Number(legrecnoStr);
      const userEdits = editedLegs[legrecno];
      if (!userEdits) continue;
      const stale = fields.filter(f => f in userEdits);
      if (stale.length > 0) filtered[legrecno] = stale;
    }
    if (Object.keys(filtered).length > 0) {
      setStaleFields(prevStale => {
        const next = { ...prevStale };
        for (const [legrecnoStr, fields] of Object.entries(filtered)) {
          const legrecno = Number(legrecnoStr);
          const existing = next[legrecno] ?? [];
          // Merge without duplicates so repeated external refreshes accumulate
          // every still-conflicting field.
          const merged = Array.from(new Set([...existing, ...fields]));
          next[legrecno] = merged;
        }
        return next;
      });
    }
  }, [orderDetails, editedLegs]);

  /**
   * Reconcile the user's staged edits against a freshly fetched copy of the order.
   *
   * Drops the staged edits that collide with whatever moved since this tab loaded,
   * records the theirs/mine rows behind the badges, and reports what drifted.
   *
   * Shared by the pre-save check and by refresh(), because the two have to agree.
   * Whichever path brings fresh data in, a staged value that was a harmless no-op
   * when it was staged must not survive into the save as a destructive write once
   * the DB has moved underneath it — refresh() used to swap orderDetails and leave
   * the edits alone, which is exactly that. (PRO 5140356: a driver stamped leg 2's
   * origin arrival + departure while a dispatcher had the pro open; blanks went back
   * over them five minutes later. Neither conflict check could see it — by then the
   * submitted `old` had been refreshed to match the DB, so there was no drift left
   * to flag, and the blanks read as a deliberate edit.)
   *
   * Deliberately does NOT touch orderDetails: the save path reports the drift to the
   * user and swaps the data as part of returning 'stale', while refresh() swaps it
   * unconditionally. Callers own that half.
   */
  const reconcileStagedEdits = useCallback((
    fresh: OrderDetails | null,
    snapshot: OrderDetails,
    opts?: {
      /** Create mode has no persisted legs to compare — skip the leg half entirely. */
      skipLegs?: boolean;
      serverConflicts?: Array<{ legrecno?: number | null; field: string }>;
    },
  ): {
    changedOnSave: Record<number, string[]>;
    changedOrderFields: string[];
    conflicts: ConflictMap;
    drifted: boolean;
  } => {
    const freshLegs = fresh?.legs || [];
    // Only compare original (positive-legrecno) legs — same filter the
    // drawer uses, because pending-new legs would otherwise look "removed"
    // and pending-removed legs would otherwise look "new".
    const originalLegsForStaleCheck = opts?.skipLegs
      ? []
      : snapshot.legs.filter(l => l.legrecno > 0);
    const changedOnSave = detectChangedFields(originalLegsForStaleCheck, freshLegs);
    // Fold in whatever the backend flagged. Normally a no-op — it and this check
    // look at the same drift — but if the server blocked on a column this list
    // exempts, the row still has to reach the badge, or the user gets a refused
    // save with nothing highlighted.
    for (const c of opts?.serverConflicts ?? []) {
      if (c.legrecno == null) continue;
      const seen = changedOnSave[c.legrecno] ?? [];
      if (!seen.includes(c.field)) changedOnSave[c.legrecno] = [...seen, c.field];
    }

    // Compare order-level fields (mirrors drawer's staleOrderFields list).
    const changedOrderFields: string[] = [];
    const orderFieldKeys: Array<keyof OrderDetails> = [
      'chassisType',
      'chassisProvider',
      'sealNumber',
      'chassisId',
      'dropReleaseComment',
      'dropReleaseDatetime',
      'comments',
      'dispatcher',
    ];
    if (fresh) {
      for (const key of orderFieldKeys) {
        const oldVal = (snapshot[key] as unknown) ?? '';
        const newVal = (fresh[key] as unknown) ?? '';
        if (oldVal !== newVal) changedOrderFields.push(key);
      }
    }

    const hasLegConflicts = Object.keys(changedOnSave).length > 0;
    const hasOrderConflicts = changedOrderFields.length > 0;
    if (!hasLegConflicts && !hasOrderConflicts) {
      return { changedOnSave, changedOrderFields, conflicts: {}, drifted: false };
    }

    // Build the conflict map BEFORE we drop the user's edits — `mine`
    // needs to capture what the user had attempted.
    const newConflicts: ConflictMap = {};
    for (const [legrecnoStr, fields] of Object.entries(changedOnSave)) {
      const legrecno = Number(legrecnoStr);
      const freshLeg = freshLegs.find(l => l.legrecno === legrecno);
      const userEdits = editedLegsRef.current[legrecno];
      for (const field of fields) {
        const key = `leg:${legrecno}:${field}`;
        if (field === '__new__' || field === '__removed__') {
          newConflicts[key] = { theirs: freshLeg ?? null, mine: undefined };
        } else {
          newConflicts[key] = {
            theirs: freshLeg
              ? (freshLeg as unknown as Record<string, unknown>)[field]
              : undefined,
            mine: userEdits
              ? (userEdits as unknown as Record<string, unknown>)[field]
              : undefined,
          };
        }
      }
    }
    for (const field of changedOrderFields) {
      newConflicts[field] = {
        theirs: (fresh as unknown as Record<string, unknown>)?.[field],
        mine: editedOrderFieldsRef.current[field],
      };
    }

    // Drop user edits for conflicting fields so fresh DB values show.
    if (mountedRef.current) {
      if (hasLegConflicts) {
        setEditedLegs(prev => {
          const updated = { ...prev };
          for (const [legrecnoStr, fields] of Object.entries(changedOnSave)) {
            const legrecno = Number(legrecnoStr);
            if (fields.includes('__new__')) {
              // New linked leg: clear driver on all other legs in the
              // same dispatch scope (their dispatchid would now be wrong).
              getDispatchScope(legrecno, freshLegs)
                .filter(scopeLegrecno => scopeLegrecno !== legrecno)
                .forEach(scopeLegrecno => {
                  if (updated[scopeLegrecno]?.driver !== undefined) {
                    const legEdits = { ...updated[scopeLegrecno] };
                    delete legEdits.driver;
                    if (Object.keys(legEdits).length === 0) delete updated[scopeLegrecno];
                    else updated[scopeLegrecno] = legEdits;
                  }
                });
            } else if (fields.includes('__removed__')) {
              delete updated[legrecno];
            } else if (fields.includes('origcode') || fields.includes('destcode')) {
              // Location changed: clear ALL edits for that leg (they
              // were for a different location and aren't relevant now).
              delete updated[legrecno];
            } else if (updated[legrecno]) {
              const legEdits = { ...updated[legrecno] };
              fields.forEach(field => {
                delete legEdits[field as keyof OrderLeg];
              });
              if (Object.keys(legEdits).length === 0) delete updated[legrecno];
              else updated[legrecno] = legEdits;
            }
          }
          return updated;
        });
      }
      if (hasOrderConflicts) {
        setEditedOrderFields(prev => {
          const updated = { ...prev };
          changedOrderFields.forEach(field => {
            delete updated[field];
          });
          return updated;
        });
      }
      setConflicts(newConflicts);
    }

    return { changedOnSave, changedOrderFields, conflicts: newConflicts, drifted: true };
  }, []);

  const refresh = useCallback(async () => {
    if (pronumber == null) return;
    // Create mode has no server row to re-read — the pronumber is the 'NEW'
    // sentinel, so this would just 404. (Once the host flips the drawer to the
    // created pro, create mode is off and refresh works normally.)
    if (createActiveRef.current) return;
    try {
      const response = await ordersService.getOrderDetails(pronumber);
      if (!mountedRef.current) return;
      // Reconcile the staged edits against what just came down, BEFORE the data
      // swaps underneath them. Without this a refresh re-pointed `old` at the new
      // DB values while leaving a now-stale staged value in place — and the save
      // then wrote it over the newer data with nothing left for either conflict
      // check to flag. See reconcileStagedEdits.
      const snapshot = orderDetailsRef.current;
      if (snapshot) reconcileStagedEdits(response.data, snapshot);
      setOrder({ pronumber } as unknown as Order);
      setOrderDetails(response.data);
      setLoadError(null);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Failed to refresh order details:', error);
    }
  }, [pronumber, reconcileStagedEdits]);

  const updateOrderField = useCallback((name: string, value: any) => {
    // Hard-block (LC only): setting the order to drop/live cascades onto the
    // delivery leg, which would strand a dispatched leg in the split-off run.
    // Prevent the change entirely and surface the guidance modal, before any
    // edit is staged. The confirm (driver-free, has-dates) case is left to the
    // leg-side trigger in updateLegField below, so it's raised in one place.
    if (name === 'isDrop') {
      const deliveryLeg = effectiveLegsRef.current.find(
        (l) => l.legAction2 === 'DELIVERY',
      );
      if (
        deliveryLeg &&
        deliveryLeg.legDrop !== value &&
        evaluateDropChange(deliveryLeg.legrecno, value)?.kind === 'blocked'
      ) {
        setDropApptClearPrompt({ kind: 'blocked' });
        return;
      }
    }

    noteOverrideEdit();
    setEditedOrderFields(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'offhire' && value === 'N' ? { redeliveryExpirationDate: '' } : {}),
    }));

    // Bidirectional sync: order's `isDrop` ↔ delivery leg's `legDrop`. When
    // the user toggles isDrop on the order form, cascade the same value to
    // the delivery leg's legDrop so both fields land in the save payload.
    // updateLegField handles the leg-side cascade (driver carry/clear, etc.)
    // via cascadeLegDropChange — we delegate to it rather than duplicating
    // that logic here. The leg→order direction is handled inside
    // updateLegField below.
    if (name === 'isDrop') {
      // Resolve the ACTUAL delivery leg from the current (effective) legs by its
      // destination action — NOT the persisted `deliveryLeg` number. Adding or
      // removing a stop before delivery renumbers the legs, so a fixed legnum
      // would cascade onto the wrong (now non-delivery) leg. This mirrors how
      // legSavePayload computes `deliveryleg` (legAction2 === 'DELIVERY'), so the
      // synced leg is the same one the save persists as the delivery leg.
      const deliveryLeg = effectiveLegsRef.current.find(
        (l) => l.legAction2 === 'DELIVERY',
      );
      if (deliveryLeg) {
        // effectiveLegs already overlays editedLegs, so legDrop is the current
        // value. Only fire if it would actually change — guards against
        // duplicate writes when the user toggles via the leg side first.
        if (deliveryLeg.legDrop !== value) {
          updateLegFieldRef.current?.(deliveryLeg.legrecno, 'legDrop', value);
        }
      }
    }
  }, []);

  // Drop a staged order-field edit, reverting it to the persisted value. Used by
  // the delete flow to un-stage an optimistic `deleted='Y'` on cancel/block so the
  // field snaps back to 'N' with no lingering "unsaved changes".
  const revertOrderField = useCallback((name: string) => {
    setEditedOrderFields(prev => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  // Forward ref so updateOrderField (defined first) can call updateLegField
  // (defined below) without a hoisting issue. Populated after both
  // callbacks are constructed.
  const updateLegFieldRef = useRef<((legrecno: number, name: string, value: any) => void) | null>(null);

  // Async-computed save-blocking errors keyed by `source` so independent
  // contributors (driver re-qual, mode/terminal check, etc.) don't clobber
  // each other. Held in a ref so validateChanges reads it synchronously
  // regardless of render timing. Passing an empty array for a source
  // removes its contribution.
  const extraSaveErrorsRef = useRef<Map<string, string[]>>(new Map());
  const setExtraSaveErrors = useCallback((source: string, errors: string[]) => {
    if (errors.length === 0) {
      extraSaveErrorsRef.current.delete(source);
    } else {
      extraSaveErrorsRef.current.set(source, errors);
    }
  }, []);

  // Holds the latest effectiveLegs for use inside updateLegField's setter
  // callback. effectiveLegs is defined later in the function body so we
  // forward-declare a ref and populate it after effectiveLegs is computed.
  const effectiveLegsRef = useRef<OrderLeg[]>([]);
  const orderDetailsRef = useRef<OrderDetails | null>(null);

  // Monotonic counter for pending-new leg legrecnos. Seeded once below
  // `Date.now()` to keep IDs visibly negative + ordered; each call
  // decrements so two rapid addLeg invocations never collide on the same
  // key (which would make editedLegs overlays leak between them).
  const nextTempLegrecnoRef = useRef(-Date.now());
  const nextTempLegrecno = (): number => {
    nextTempLegrecnoRef.current -= 1;
    return nextTempLegrecnoRef.current;
  };
  orderDetailsRef.current = orderDetails;

  // Kept in refs so the stable updateLegField / confirmDropApptClear callbacks
  // read the latest values without widening their dependency arrays. The
  // drop→clear-appt-dates prompt is gated purely on driver-field access
  // (`leg-driver-field`, via `canEditDriverField`): anyone who can edit the
  // driver field can remove a driver assignment themselves without coordinating
  // with a dispatcher, so they don't need the prompt. It fires only for users
  // WITHOUT that access.
  const showDropApptPromptRef = useRef(false);
  showDropApptPromptRef.current = !options?.canEditDriverField;
  const dropApptClearPromptRef = useRef<DropApptClearPrompt | null>(null);
  dropApptClearPromptRef.current = dropApptClearPrompt;

  // Decide what happens when a non-driver-editor sets `legrecno` to drop=Y —
  // the single source of truth for both entry points (the grid Drop badge and
  // the order-level isDrop toggle). Returns:
  //   - 'blocked': a dispatched leg would be stranded in the split-off run. The
  //     user can clear neither that driver (no leg-driver-field access) nor its
  //     (locked) dates, so the whole drop/live change is DISALLOWED — callers
  //     must not apply it.
  //   - 'confirm': the split-off run is driver-free but carries appt dates —
  //     apply the change, then offer to clear those dates.
  //   - null: nothing to prompt (the user can edit the driver field, not an
  //     N→Y transition, or the split-off run has neither a dispatched leg nor
  //     any appt dates).
  // Reads pre-change effective legs via refs, so it's safe to call before the
  // edit is applied.
  const evaluateDropChange = useCallback(
    (legrecno: number, value: unknown): DropApptClearPrompt | null => {
      if (!showDropApptPromptRef.current) return null;
      const legs = effectiveLegsRef.current;
      const cur = legs.find((l) => l.legrecno === legrecno);
      const oldDrop = (cur?.legDrop as string | undefined) || 'N';
      const newDrop = (value as string) || 'N';
      if (oldDrop === 'Y' || newDrop !== 'Y') return null; // only N→Y
      const affected = getLegsLeavingScopeOnDrop(legrecno, legs);
      // A dispatched leg anywhere in the split-off run blocks the change
      // outright, regardless of appt dates (the driver can't be cleared without
      // leg-driver-field access). Mirrors the server appt-lock guard's
      // driver-free requirement.
      if (affected.some(isLegDispatched)) return { kind: 'blocked' };
      const withDates = affected.filter(
        (l) => hasApptDate(l.origApptDate) || hasApptDate(l.destApptDate),
      );
      if (withDates.length === 0) return null;
      return {
        kind: 'confirm',
        legrecnos: withDates.map((l) => l.legrecno),
        legs: withDates.map((l) => ({
          legrecno: l.legrecno,
          legnum: l.legnum,
          label: dropClearLegLabel(l),
        })),
      };
    },
    [],
  );

  // Auto-fill missing appt dates across the dispatch scope of the leg a driver
  // was just assigned to. Delegates to the same scope walk the legs grid
  // (maybeAutoFillDates) and the save-time safety net use, so all three passes
  // agree on the scope boundaries and the fill sources.
  //
  // The walk runs over the EFFECTIVE legs (saved minus pending-removed, plus
  // pending-new, in on-screen order) with the in-flight edits map overlaid.
  // This used to mix saved-leg indices with effective-leg indices: inserting a
  // leg ahead of the trigger (a chassis split before the pickup) shifted the
  // saved-leg slice by one, so it ran past the drop boundary and stamped the
  // empty-return leg with the loaded leg's dates — which makes a still-loaded
  // box look dispatchable as an empty (drop-and-hook report, 2026-10-01).
  //
  // Inputs are passed in (rather than read from closure) so the function can
  // run inside the setEditedLegs setter callback against the freshest edits
  // map; overlaying that map matters because the location cell and the 000
  // button fire several writes in one batch before effectiveLegs re-renders.
  const computeAutoFill = (
    triggerLegrecno: number,
    currentEdits: Record<number, Partial<OrderLeg>>,
    effLegs: OrderLeg[],
  ): Array<{ legrecno: number; origApptDate?: string; destApptDate?: string }> => {
    const overlaid = effLegs.map(l => ({ ...l, ...(currentEdits[l.legrecno] || {}) }));
    const fills = computeScopeApptFills(triggerLegrecno, overlaid);
    if (!fills) return [];
    return Object.entries(fills).map(([legrecno, fill]) => ({
      legrecno: Number(legrecno),
      ...fill,
    }));
  };

  // Mirror leg-field edits across adjacent stops (port of the drawer's
  // updateLegField cascade ladder at OrderDetailsModalContent.tsx:1689-1882).
  // Mutates `next` in place and returns nothing. Reads legDrop from the
  // edits-overlay (matches drawer behavior so a same-session N→Y toggle
  // unlinks the cascade immediately).
  const cascadeOnFieldUpdate = (
    legrecno: number,
    field: string,
    value: any,
    next: Record<number, Partial<OrderLeg>>,
  ): void => {
    // Use the effective leg list (orderDetails.legs + pendingNewLegs) so the
    // cascade picks up pending-new legs. Without this, editing a pending-new
    // leg's destination wouldn't propagate to the next leg's origin because
    // findIndex would return -1.
    const legs = effectiveLegsRef.current;
    if (!legs.length) return;
    const idx = legs.findIndex(l => l.legrecno === legrecno);
    if (idx === -1) return;
    const prevLeg = idx > 0 ? legs[idx - 1] : null;
    const nextLeg = idx < legs.length - 1 ? legs[idx + 1] : null;
    const currentLeg = legs[idx];

    const legDropOf = (l: OrderLeg): string | undefined =>
      (next[l.legrecno]?.legDrop as string | undefined) ?? l.legDrop;

    switch (field) {
      case 'origcode':
        if (prevLeg) next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), destcode: value };
        break;
      case 'destcode':
        if (nextLeg) next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), origcode: value };
        break;
      case 'origName':
        if (prevLeg) next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), destName: value };
        break;
      case 'origCityState':
        if (prevLeg) next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), destCityState: value };
        break;
      case 'destName':
        if (nextLeg) next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), origName: value };
        break;
      case 'destCityState':
        if (nextLeg) next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), origCityState: value };
        break;
      case 'origApptDate':
        if (prevLeg && legDropOf(prevLeg) !== 'Y') {
          next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), destApptDate: value };
        }
        break;
      case 'destApptDate':
        if (nextLeg && legDropOf(currentLeg) !== 'Y') {
          next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), origApptDate: value };
        }
        break;
      case 'origApptTime1':
      case 'origApptTime2': {
        const destField = field === 'origApptTime1' ? 'destApptTime1' : 'destApptTime2';
        if (prevLeg && legDropOf(prevLeg) !== 'Y') {
          next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), [destField]: value };
        }
        break;
      }
      case 'destApptTime1':
      case 'destApptTime2': {
        const origField = field === 'destApptTime1' ? 'origApptTime1' : 'origApptTime2';
        if (nextLeg && legDropOf(currentLeg) !== 'Y') {
          next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), [origField]: value };
        }
        break;
      }
      case 'legtype':
        if (prevLeg) next[prevLeg.legrecno] = { ...(next[prevLeg.legrecno] || {}), legAction2: value };
        break;
      case 'legAction2':
        if (nextLeg) next[nextLeg.legrecno] = { ...(next[nextLeg.legrecno] || {}), legtype: value };
        break;
      default:
        break;
    }
  };

  // What assigning `driver` to the legs in `targetLegrecnos` does beyond the
  // driver itself, in place on `next`: back-fill empty appt dates across the
  // trigger leg's dispatch scope, then auto-stamp the arrival. Shared by a typed
  // driver and one inherited by setting a leg Live (bugfix/#34227), so the two
  // can't drift apart.
  const applyDriverAssignedFills = (
    triggerLegrecno: number,
    driver: string,
    targetLegrecnos: number[],
    next: Record<number, Partial<OrderLeg>>,
  ): void => {
    const od = orderDetailsRef.current;
    if (!driver || !od) return;

    // Auto-fill empty appt dates in scope when a driver is assigned.
    // Mirrors drawer's handleDriverAssigned -> autoFillDatesIfNeeded
    // (OrderDetailsModalContent.tsx:2133-2143).
    const writes = computeAutoFill(triggerLegrecno, next, effectiveLegsRef.current);
    for (const w of writes) {
      const edits = (next[w.legrecno] || {}) as Record<string, unknown>;
      // An explicitly staged blank is an INTENT, not a hole to backfill —
      // the same rule the save-time net applies (bugfix/#33627), and it
      // belongs here for the same reason: computeAutoFill walks the
      // EDIT-OVERLAID legs, so a date the user just cleared is
      // indistinguishable from one that was never set. Without this,
      // assigning a driver anywhere in the scope put a cleared date back.
      // A leg that was already blank carries no key here, so the backfill
      // this pass exists for still runs.
      const keep = (field: 'origApptDate' | 'destApptDate', v?: string) =>
        v !== undefined && edits[field] !== '' ? { [field]: v } : {};
      next[w.legrecno] = {
        ...edits,
        ...keep('origApptDate', w.origApptDate),
        ...keep('destApptDate', w.destApptDate),
      };
    }

    // Auto-set the arrival datetime on driver assign (port of v1
    // leg_setarrivaldate, js/cms.js:5527). Runs AFTER the appt-date auto-fill
    // above so it can key off the just-filled destApptDate, matching v1's
    // leg_setapptdate -> leg_setarrivaldate ordering. Applied to every leg the
    // driver landed on, each using its own (effective) appt date + arrival.
    //
    // The dest appt date drives the arrival stamp. computeAutoFill above
    // walks the effective legs, so `next` already carries any date it just
    // backfilled — on saved and pending-new legs alike (the pending-new case
    // used to need a separate effective-legs lookup here; Lee, 2026-07-21).
    // No-op for GWI drivers (computeDriverArrival returns null).
    const effLegs = effectiveLegsRef.current;
    for (const targetLegrecno of targetLegrecnos) {
      const baseLeg =
        effLegs.find(l => l.legrecno === targetLegrecno) ||
        od.legs.find(l => l.legrecno === targetLegrecno);
      if (!baseLeg) continue;
      const merged = next[targetLegrecno] || {};
      const destApptDate = (merged.destApptDate ?? baseLeg.destApptDate) as string;
      const destArrivalDateTime = (merged.destArrivalDateTime ??
        baseLeg.destArrivalDateTime) as string | null;
      const newArrival = computeDriverArrivalDateTime({
        dispatcher: od.dispatcher,
        driver,
        destApptDate,
        destArrivalDateTime,
      });
      if (newArrival !== null) {
        next[targetLegrecno] = {
          ...(next[targetLegrecno] || {}),
          destArrivalDateTime: newArrival,
        };
      }
    }
  };

  const updateLegField = useCallback((legrecno: number, name: string, value: any) => {
    noteOverrideEdit();
    // Drop=Y → offer LCs the chance to clear appt dates on the legs that just
    // split out of the dispatch grouping. Both the grid Drop badge and the
    // order-level isDrop toggle (which delegates here via updateLegFieldRef)
    // funnel through this branch, so it covers both directions. Computed from
    // the pre-update effective legs and raised OUTSIDE the setEditedLegs
    // updater below — never call setState from inside a state updater.
    if (name === 'legDrop') {
      const prompt = evaluateDropChange(legrecno, value);
      if (prompt) {
        setDropApptClearPrompt(prompt);
        // 'blocked' hard-stops the toggle: return before setEditedLegs so
        // neither the legDrop edit, its driver cascade, nor the leg→order
        // isDrop mirror (all inside the setter below) are applied. The grid
        // Drop badge is prop-driven, so skipping the edit leaves it on 'Live'.
        if (prompt.kind === 'blocked') return;
        // 'confirm' falls through: apply the drop, then the modal offers to
        // clear the split-off legs' appt dates.
      }
    }

    // #33649 — typing a stop to DELIVERY demotes the order's existing DELIVERY
    // stop to STOPOFF, so a re-delivery can be added without first clearing a
    // delivery the user may have no unlocked cell for (paid-out leg). Planned
    // HERE, off the pre-edit effective legs, rather than inside the setter: the
    // setter must stay a pure state transform (StrictMode invokes it twice), and
    // the notice below has to fire exactly once. See planDeliveryDemotion.
    const deliveryDemotion =
      name === 'legtype' || name === 'legAction2'
        ? planDeliveryDemotion({
            legs: effectiveLegsRef.current,
            legrecno,
            field: name,
            value,
          })
        : null;

    // Port of legacy cms.js leg_movetype / leg_movetype2: a leg's destination Move Type
    // (legAction2) transitioning from anything to "REJECTED" pops a pre-set "REJECTED" /
    // Driver-Ticket=Y trouble ticket on the host. #33683 made that ticket MANDATORY, so
    // backing out of the modal has to take the REJECTED with it — which means capturing
    // the staged edits HERE, before the write, and handing them to the host to revert to.
    //
    // ── The mandatory-gate contract, shared with the delivery-appointment reschedule ──
    // Both gates promise the same thing: you cannot leave the modal with the triggering
    // change staged and no record behind it. They reach it differently, and the WIDGET is
    // what decides which way. The reschedule (LegDateCell in LegsGrid) defers — it never
    // stages the pick until the reschedule saves — because DateTimePicker holds the pick
    // in its own state, so a deferred cancel leaves the cell out of sync and needs a
    // remount (`pickerNonce`) to snap back. The Move Type <select> (LegSelectCell) instead
    // mirrors the staged value through a useEffect, so putting the staged value back
    // un-paints the cell on its own — snapshot-and-revert is the cheaper half here, and it
    // unwinds the cascades below (notably legtype → previous-leg legAction2) for free
    // rather than making a field-by-field undo re-derive them.
    //
    // Read off the PRE-edit effective legs, same as planDeliveryDemotion above, so a leg
    // that is already REJECTED doesn't re-fire the ticket on an unrelated edit.
    let rejectedLegrecno: number | null = null;
    if (name === 'legAction2' || name === 'legtype') {
      const isRejected = (v: unknown) =>
        String(v ?? '').trim().toUpperCase() === 'REJECTED';
      if (isRejected(value)) {
        const legs = effectiveLegsRef.current;
        const idx = legs.findIndex((l) => l.legrecno === legrecno);
        if (name === 'legAction2') {
          const cur = idx >= 0 ? legs[idx] : undefined;
          if (cur && !isRejected(cur.legAction2)) rejectedLegrecno = legrecno;
        } else {
          // A legtype edit cascades the new value onto the PREVIOUS leg's legAction2 —
          // fire only when that previous leg exists and wasn't already REJECTED (matches
          // the cascade's own prevLeg guard).
          const prevLeg = idx > 0 ? legs[idx - 1] : null;
          if (prevLeg && !isRejected(prevLeg.legAction2)) rejectedLegrecno = prevLeg.legrecno;
        }
      }
    }
    // snapshotEdits is a stable, empty-dep useCallback declared below; this closure only
    // runs from an event handler, long after the component body has initialized it.
    const rejectedRevertSnapshot = rejectedLegrecno != null ? snapshotEdits() : null;

    setEditedLegs(prev => {
      // Special case: legDrop toggles cascade differently (drop driver from
      // legs leaving scope on N->Y; carry source-leg's driver to legs joining
      // scope on Y->N). Delegate the entire write to cascadeLegDropChange,
      // which handles both the primary write and the side cascade. Mirrors
      // the drawer at OrderDetailsModalContent.tsx:1865-1870.
      //
      // Also mirror legDrop edits on the delivery leg back to the order's
      // `isDrop` field so the two stay in sync — see the matching cascade
      // in updateOrderField for the order→leg direction. The setEditedLegs
      // setter doesn't see editedOrderFields, so we schedule the order-side
      // write via the dedicated setter (it's a no-op if value is unchanged).
      const orderDetails = orderDetailsRef.current;
      if (name === 'legDrop' && orderDetails) {
        // Sync back to the order's isDrop only when the edited leg is the ACTUAL
        // delivery stop (destination action DELIVERY) in the current effective
        // legs — not a fixed `deliveryLeg` number, which goes stale when a stop
        // is added/removed before delivery. Mirrors legSavePayload's deliveryleg
        // resolution (legAction2 === 'DELIVERY') and the order→leg cascade above.
        const effLeg = effectiveLegsRef.current.find((l) => l.legrecno === legrecno);
        if (effLeg?.legAction2 === 'DELIVERY') {
          // Schedule the order-side write outside the setEditedLegs setter.
          // Using setEditedOrderFields directly avoids re-entering
          // updateOrderField (which would re-fire the leg cascade).
          setEditedOrderFields((ofPrev) =>
            ofPrev.isDrop === value ? ofPrev : { ...ofPrev, isDrop: value },
          );
        }
        const cascaded = cascadeLegDropChange(legrecno, value as string, orderDetails.legs, prev);
        // A driverless leg set Live takes the following group's driver, exactly as
        // if it had been typed — same appt back-fill and arrival stamp
        // (bugfix/#34227). Walks the ON-SCREEN legs: an inserted, unsaved split
        // is the motivating case, and cascadeLegDropChange's saved legs don't
        // contain it.
        const inherit = planLiveDriverInherit(
          legrecno, value as string, effectiveLegsRef.current, cascaded,
        );
        if (!inherit) return cascaded;
        const next: Record<number, Partial<OrderLeg>> = { ...cascaded };
        for (const r of inherit.legrecnos) {
          next[r] = { ...(next[r] || {}), driver: inherit.driver };
        }
        applyDriverAssignedFills(legrecno, inherit.driver, inherit.legrecnos, next);
        return next;
      }

      // Determine which legs the write applies to.
      // For driver / sendToGwiMove: cascade across the dispatch scope so the
      // save payload carries the value on every leg in the scope (matches the
      // drawer's behavior at OrderDetailsModalContent.tsx:1689-1701). All other
      // fields write only to the target legrecno.
      const targetLegrecnos: number[] =
        (name === 'driver' || name === 'sendToGwiMove') && effectiveLegsRef.current.length > 0
          ? getDispatchScope(legrecno, effectiveLegsRef.current)
          : [legrecno];

      const next: Record<number, Partial<OrderLeg>> = { ...prev };
      for (const targetLegrecno of targetLegrecnos) {
        next[targetLegrecno] = {
          ...(prev[targetLegrecno] || {}),
          [name]: value,
        };
      }

      // Mirror the edit onto adjacent legs where the drawer cascades. Skipped
      // for driver/sendToGwiMove (handled by their scope cascade above) and
      // legDrop (handled by the early return into cascadeLegDropChange above —
      // the guard here is defense in depth). All other supported fields
      // delegate to cascadeOnFieldUpdate.
      if (name !== 'driver' && name !== 'sendToGwiMove' && name !== 'legDrop') {
        cascadeOnFieldUpdate(legrecno, name, value, next);
      }

      // Appt-date back-fill + arrival auto-stamp for the driver just cascaded
      // across the scope (see applyDriverAssignedFills).
      if (name === 'driver' && value) {
        applyDriverAssignedFills(legrecno, String(value), targetLegrecnos, next);
      }

      // When a stop's move type is (re)typed to DELIVERY, force the stop's
      // LOCATION to the underlying (saved) delivery location. This powers the
      // re-delivery workflow (a new delivery stop inherits the location
      // automatically) AND closes the permission bypass: a user without
      // `leg-delivery-location` cannot move a delivery by flipping it to STOPOFF,
      // editing the location, and flipping it back — the flip-back overwrites
      // their edit with the saved location, which then re-locks. Write-access
      // users can re-edit.
      //
      // A delivery lives at a BOUNDARY (one leg's destination == the next leg's
      // origin), and either side's move-type dropdown can set it:
      //   - legAction2 on leg N → boundary (leg N dest, leg N+1 orig)
      //   - legtype    on leg N → boundary (leg N-1 dest, leg N orig)
      // so we write the location to BOTH sides of whichever boundary was typed.
      //
      // Source is the SAVED delivery leg (orderDetails), NOT the current
      // effective legs: the on-screen delivery may already have been converted
      // away and moved, so the persisted location is the reliable source. It
      // INTENTIONALLY includes the leg being edited so the flip-back self-heals.
      // An order with no saved delivery has nothing to copy, so the initial
      // delivery is left for the user to set. Only location fields copy — appt
      // dates / driver stay empty on the leg.
      if (
        (name === 'legAction2' || name === 'legtype') &&
        String(value ?? '').trim().toUpperCase() === 'DELIVERY'
      ) {
        const savedDelivery = (orderDetailsRef.current?.legs ?? []).find(
          (l) => String(l.legAction2 || '').toUpperCase() === 'DELIVERY' && l.destcode,
        );
        if (savedDelivery) {
          const setDest = (recno: number) => {
            next[recno] = {
              ...(next[recno] || {}),
              destcode: savedDelivery.destcode,
              destName: savedDelivery.destName,
              destCityState: savedDelivery.destCityState,
              destcoordinates: savedDelivery.destcoordinates,
            };
          };
          const setOrig = (recno: number) => {
            next[recno] = {
              ...(next[recno] || {}),
              origcode: savedDelivery.destcode,
              origName: savedDelivery.destName,
              origCityState: savedDelivery.destCityState,
              origcoordinates: savedDelivery.destcoordinates,
            };
          };
          const legs = effectiveLegsRef.current;
          const idx = legs.findIndex((l) => l.legrecno === legrecno);
          const prevLeg = idx > 0 ? legs[idx - 1] : null;
          const nextLeg = idx >= 0 && idx < legs.length - 1 ? legs[idx + 1] : null;
          if (name === 'legAction2') {
            // Delivery on THIS leg's destination (== next leg's origin).
            setDest(legrecno);
            if (nextLeg) setOrig(nextLeg.legrecno);
          } else {
            // legtype: delivery on THIS leg's origin (== prev leg's destination).
            setOrig(legrecno);
            if (prevLeg) setDest(prevLeg.legrecno);
          }
        }
      }

      // Apply the DELIVERY demotion planned above (#33649). Runs LAST so it sees
      // the typed value and its boundary cascade already in `next` — the planner
      // excludes the stop being typed, so these writes never collide with them.
      if (deliveryDemotion) {
        for (const w of deliveryDemotion.writes) {
          next[w.legrecno] = { ...(next[w.legrecno] || {}), [w.field]: w.value };
        }
      }

      return next;
    });

    // Tell the host what just moved, so it can toast it. Outside the setter for
    // the StrictMode reason noted above.
    if (deliveryDemotion) {
      onDeliveryDemotedRef.current?.({ legnums: deliveryDemotion.demotedLegnums });
    }

    // Planned above the write (see the mandatory-gate note there). Fired outside the
    // setter for the same StrictMode reason as the demotion notice: the setter runs
    // twice, this must not.
    if (rejectedLegrecno != null && rejectedRevertSnapshot) {
      onLegActionRejectedRef.current?.({
        legrecno: rejectedLegrecno,
        revertSnapshot: rejectedRevertSnapshot,
      });
    }
    // computeAutoFill reads orderDetails and effectiveLegs via refs, so this
    // callback's identity stays stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate the forward ref so updateOrderField's isDrop cascade can reach
  // updateLegField. The ref is assigned each render; consumers always see
  // the latest callback identity.
  updateLegFieldRef.current = updateLegField;

  // ── Admin Override edit snapshots (#33501) ────────────────────────────────
  // Turning override OFF undoes the edits the override ENABLED — and only those. A
  // blanket resetEdits would also throw away work staged before override was switched
  // on, which the user never agreed to lose. So the host snapshots the staged edits at
  // the moment override goes on and restores that snapshot if the user discards.
  const snapshotEdits = useCallback((): EditSnapshot => ({
    orderFields: { ...editedOrderFieldsRef.current },
    // One level deeper: editedLegs values are per-leg partials, and a shallow copy
    // would alias them, so a later leg edit would mutate the "before" picture.
    legs: Object.fromEntries(
      Object.entries(editedLegsRef.current).map(([k, v]) => [k, { ...v }]),
    ),
    newLegs: [...pendingNewLegsRef.current],
    removedLegrecnos: [...pendingRemovedLegrecnosRef.current],
    overrideUsed: adminOverrideUsedRef.current,
  }), []);

  /** How many field-level edits have been staged since `snap`. Drives the confirm
   *  copy, so it counts what a user would call "a change": one per order field, one per
   *  leg field, plus any legs added or removed. */
  const countEditsSince = useCallback((snap: EditSnapshot | null): number => {
    if (!snap) return 0;
    let n = 0;
    const cur = editedOrderFieldsRef.current;
    for (const k of Object.keys(cur)) {
      if (!Object.is(cur[k], snap.orderFields[k])) n += 1;
    }
    const curLegs = editedLegsRef.current;
    for (const [recno, fields] of Object.entries(curLegs)) {
      const before = snap.legs[recno] ?? {};
      for (const f of Object.keys(fields)) {
        if (!Object.is((fields as any)[f], (before as any)[f])) n += 1;
      }
    }
    n += Math.max(0, pendingNewLegsRef.current.length - snap.newLegs.length);
    n += Math.max(0, pendingRemovedLegrecnosRef.current.length - snap.removedLegrecnos.length);
    return n;
  }, []);

  /** Restore staged edits to `snap`, dropping everything staged since. */
  const revertToSnapshot = useCallback((snap: EditSnapshot | null) => {
    if (!snap) return;
    setEditedOrderFields({ ...snap.orderFields });
    setEditedLegs(
      Object.fromEntries(
        Object.entries(snap.legs).map(([k, v]) => [Number(k), { ...v }]),
      ),
    );
    setPendingNewLegs([...snap.newLegs]);
    setPendingRemovedLegrecnos([...snap.removedLegrecnos]);
    // The edits staged SINCE the snapshot are gone, so the override that permitted them
    // is no longer riding this save. But the snapshot itself can already contain override
    // edits — a second override session, or the #33683 REJECTED revert, which snapshots
    // mid-edit rather than at override-on — and those are still staged. So restore what
    // the latch held then rather than blanking it, or they would save tagged `user` and
    // lose the audit trail the feature exists for.
    adminOverrideUsedRef.current = snap.overrideUsed;
  }, []);

  const resetEdits = useCallback(() => {
    adminOverrideUsedRef.current = false;
    setEditedOrderFields({});
    setEditedLegs({});
    setPendingNewLegs([]);
    setPendingRemovedLegrecnos([]);
    setSaveError(null);
    setValidationAttempted(false);
    setConflicts({});
    setStaleFields({});
    invoiceLineItemsRef.current = null;
    setInvoiceDirty(false);
    docInvoiceFlagsRef.current = {};
    setDocInvoiceFlags({});
  }, []);

  const clearStaleField = useCallback((legrecno: number, field: string) => {
    setStaleFields(prev => {
      const fields = prev[legrecno];
      if (!fields || !fields.includes(field)) return prev;
      const next = { ...prev };
      const remaining = fields.filter(f => f !== field);
      if (remaining.length === 0) delete next[legrecno];
      else next[legrecno] = remaining;
      return next;
    });
  }, []);

  // Drop driver/sendToGwiMove edits whenever the effective dispatcher is
  // non-GWI — CMS would silently reject these and the user would see a
  // misleading save state. Mirrors drawer at OrderDetailsModalContent.tsx:1447-1465.
  useEffect(() => {
    if (options?.effectiveDispatcherIsGwi !== false) return;
    setEditedLegs(prev => {
      let mutated = false;
      const updated: Record<number, Partial<OrderLeg>> = {};
      for (const [key, edits] of Object.entries(prev)) {
        if (edits && ('driver' in edits || 'sendToGwiMove' in edits)) {
          const { driver, sendToGwiMove, ...rest } = edits;
          mutated = true;
          if (Object.keys(rest).length > 0) {
            updated[Number(key)] = rest;
          }
        } else {
          updated[Number(key)] = edits;
        }
      }
      return mutated ? updated : prev;
    });
  }, [options?.effectiveDispatcherIsGwi]);

  // On non-GWI → GWI transition, re-apply the user's defaultSendToGwiMove
  // preference. Mirrors drawer at OrderDetailsModalContent.tsx:1471-1490.
  const prevDispatcherIsGwiRef = useRef<boolean | null>(null);
  useEffect(() => {
    const isGwi = options?.effectiveDispatcherIsGwi;
    const prev = prevDispatcherIsGwiRef.current;
    prevDispatcherIsGwiRef.current = isGwi ?? null;
    if (prev !== false || isGwi !== true) return;
    if (!options?.defaultSendToGwiMove) return;
    if (!orderDetails?.legs) return;
    const legs = orderDetails.legs;
    setEditedLegs(prevEdits => {
      const updated = { ...prevEdits };
      legs.forEach(leg => {
        const edits = updated[leg.legrecno] || {};
        const effectiveSend = 'sendToGwiMove' in edits ? edits.sendToGwiMove : leg.sendToGwiMove;
        if (!effectiveSend) {
          updated[leg.legrecno] = { ...edits, sendToGwiMove: true };
        }
      });
      return updated;
    });
  }, [options?.effectiveDispatcherIsGwi, options?.defaultSendToGwiMove, orderDetails]);

  // Pre-populate sendToGwiMove from the user's default when an order loads.
  // Mirrors the retired drawer (OrderDetailsModalContent.tsx:671-676): with the
  // preference on, stage sendToGwiMove:true for every leg not already sending,
  // so a freshly opened order is ready to dispatch to gwiMove and the per-leg
  // toggles reflect the preference (the missing half of this feature on /pro —
  // only the non-GWI → GWI re-apply above had been ported).
  //
  // Runs once per loaded order, keyed on the loaded pronumber via a ref rather
  // than on `orderDetails` identity, so a background refresh / stale-field
  // update never re-stages edits and clobbers the user's in-flight changes.
  // Skipped for non-GWI dispatchers (CMS rejects those sends); the non-GWI →
  // GWI transition above covers a later switch to a GWI dispatcher.
  const prepopulatedSendProRef = useRef<number | null>(null);
  useEffect(() => {
    const loadedPro = orderDetails?.pronumber ?? null;
    if (loadedPro == null) return;
    if (prepopulatedSendProRef.current === loadedPro) return;
    if (options?.effectiveDispatcherIsGwi === false) return;
    if (!options?.defaultSendToGwiMove) return;
    prepopulatedSendProRef.current = loadedPro;
    const legs = orderDetails?.legs;
    if (!legs || legs.length === 0) return;
    setEditedLegs(prevEdits => {
      const updated = { ...prevEdits };
      legs.forEach(leg => {
        const edits = updated[leg.legrecno] || {};
        const effectiveSend = 'sendToGwiMove' in edits ? edits.sendToGwiMove : leg.sendToGwiMove;
        if (!effectiveSend) {
          updated[leg.legrecno] = { ...edits, sendToGwiMove: true };
        }
      });
      return updated;
    });
  }, [orderDetails, options?.effectiveDispatcherIsGwi, options?.defaultSendToGwiMove]);

  // A staged Invoice flag makes the ORDER dirty — the Save button appears and the
  // navigate-away guard fires — because the order save is now the only thing that
  // writes it.
  const docFlagsDirty = Object.keys(docInvoiceFlags).length > 0;

  const hasChanges =
    Object.keys(editedOrderFields).length > 0 ||
    Object.keys(editedLegs).length > 0 ||
    pendingNewLegs.length > 0 ||
    pendingRemovedLegrecnos.length > 0 ||
    invoiceDirty ||
    docFlagsDirty;

  // Like `hasChanges`, but ignores legs whose ONLY staged edit is the
  // `sendToGwiMove` dispatch flag. The "Default Send to GWI Move" preference
  // auto-stages `sendToGwiMove: true` on order load (see the pre-populate
  // effect above) with no user action, which makes a freshly-opened order look
  // dirty. The Reschedule Requested trouble-ticket gate uses this flag instead
  // of `hasChanges` so that dispatch flag — which never affects the reschedule
  // side effect (it reads only the order's saved delivery date) — doesn't
  // produce a false "unsaved changes" warning. A leg edited in any other field
  // (or alongside sendToGwiMove) still counts as a real change.
  const hasChangesIgnoringDispatchFlags =
    Object.keys(editedOrderFields).length > 0 ||
    pendingNewLegs.length > 0 ||
    pendingRemovedLegrecnos.length > 0 ||
    invoiceDirty ||
    docFlagsDirty ||
    Object.values(editedLegs).some(edits =>
      Object.keys(edits).some(field => field !== 'sendToGwiMove'),
    );

  // Does a leg's staged edit look exactly like the auto-staged Send-to-GWI-Move
  // default (rather than a user action)? True only when the ONLY staged key is
  // `sendToGwiMove: true`, the preference is on, the dispatcher isn't a non-GWI
  // one (both required for the pre-populate effects above to have run), and the
  // loaded leg wasn't already sending — i.e., it matches precisely what those
  // effects stamp. A user turning the flag OFF (value !== true) fails the check
  // and is treated as a real change.
  const legEditIsAutoStagedSend = (legrecno: string, edits: Partial<OrderLeg>): boolean => {
    const keys = Object.keys(edits);
    if (keys.length !== 1 || keys[0] !== 'sendToGwiMove') return false;
    if (edits.sendToGwiMove !== true) return false;
    if (!options?.defaultSendToGwiMove) return false;
    if (options?.effectiveDispatcherIsGwi === false) return false;
    const original = orderDetails?.legs?.find(l => l.legrecno === Number(legrecno));
    return !original?.sendToGwiMove;
  };

  // Like `hasChanges`, but a leg whose only edit is an auto-staged send default
  // (see above) doesn't count — so opening & closing a pro with the preference
  // on raises no false close warning, while a real user edit still does.
  const hasChangesIgnoringAutoStagedSend =
    Object.keys(editedOrderFields).length > 0 ||
    pendingNewLegs.length > 0 ||
    pendingRemovedLegrecnos.length > 0 ||
    invoiceDirty ||
    docFlagsDirty ||
    Object.entries(editedLegs).some(
      ([legrecno, edits]) => !legEditIsAutoStagedSend(legrecno, edits),
    );

  // Memoized "what the user sees" leg list:
  //   1. originalLegs sorted by their loaded legnum, minus pending-removed
  //   2. walk pendingNewLegs in insertion order — each entry places its leg
  //      at the position implied by its `insert` ref (after a legrecno, at
  //      the start, or at the end of the current composition)
  //   3. overlay editedLegs[l.legrecno] per-field
  //   4. resequence legnum 1..N for display
  const effectiveLegs = useMemo<OrderLeg[]>(() => {
    const originalLegs = (orderDetails?.legs ?? [])
      .slice()
      .sort((a, b) => a.legnum - b.legnum);
    // See composeLegOrder: inserts (order-independent) then removals, so a leg
    // anchored to a removed or later-inserted pending leg keeps its slot.
    const composed = composeLegOrder(
      originalLegs, pendingNewLegs, new Set(pendingRemovedLegrecnos),
    );
    return composed.map((l, idx) => ({
      ...l,
      ...(editedLegs[l.legrecno] || {}),
      legnum: idx + 1,
    }));
  }, [orderDetails, pendingNewLegs, pendingRemovedLegrecnos, editedLegs]);

  // Keep the ref in sync so updateLegField's stable-identity callback can see
  // the freshest effective leg list when running auto-fill.
  effectiveLegsRef.current = effectiveLegs;

  // Live-form context for the quote spyglass (GET /quotes/lookup-for-pro).
  //
  // V1 built its whole `quotevars` string out of the OPEN FORM — movetype,
  // billto, ratezone, the delivery/transload leg codes, otr, customercode all
  // read straight off editrecordform (js/cms.js:2950-2998) — so "Find Quote"
  // answered for edits the user had not saved yet. Our endpoint derived the same
  // context from the saved row instead, which meant picking a Rate Zone and
  // clicking the spyglass still reported the PRO had none.
  //
  // Built here rather than in the views because the leg-position juggling needs
  // effectiveLegs (edited + structurally composed), and two surfaces consume it
  // (the Invoicing tab's Quote # picker and the Accessorial modal's).
  const quoteLookupContext = useMemo<QuoteLookupContext | null>(() => {
    if (!orderDetails) return null;
    const eff = (key: string): unknown =>
      editedOrderFields[key] !== undefined
        ? editedOrderFields[key]
        : (orderDetails as Record<string, any>)[key];
    const str = (v: unknown): string => (v == null ? '' : String(v).trim());
    const code = (v: unknown): number => Number(str(v)) || 0;

    // `deliveryLeg` holds a LEGNUM, and effectiveLegs renumbers legnum 1..n as it
    // composes, so this resolves against the same numbering the user sees.
    const deliveryLegNum = code(eff('deliveryLeg'));
    const deliveryLeg = effectiveLegs.find((l) => l.legnum === deliveryLegNum);
    // V1 findlegs('legaction2', 'TRANSLOAD', 'ADD PRODUCT', 'REMOVE PRODUCT'),
    // lowest legnum first — effectiveLegs is already in legnum order.
    const transloadLeg = effectiveLegs.find((l) =>
      ['TRANSLOAD', 'ADD PRODUCT', 'REMOVE PRODUCT'].includes(
        str(l.legAction2).toUpperCase(),
      ),
    );

    return {
      movetype: str(eff('moveType')),
      ratezone: str(eff('templateType')),
      billto: code(eff('billTo')),
      customercode: code(eff('customer')),
      otr: str(eff('otr')),
      deliverycode: String(code(deliveryLeg?.destcode)),
      transloadcode: String(code(transloadLeg?.destcode)),
    };
  }, [orderDetails, editedOrderFields, effectiveLegs]);

  // --- Structural leg add/remove (ported from OrderDetailsModalContent) ----
  // The hook's leg model is composition-based (originalLegs + pendingNewLegs +
  // editedLegs overlay) rather than the drawer's mutate-localOrderDetails
  // approach, so split-leg edits land in editedLegs rather than as direct
  // mutations on a leg's underlying. Inheritance, drop-scope carry, and
  // auto-fill rules are unchanged.

  const addLeg = useCallback(
    (type: 'first' | 'mid' | 'last', target: number) => {
      if (!orderDetails) return;
      // Street-turned Import: never stage a leg. The grid disables its controls
      // and the backend refuses the save; this covers any other caller (hotkeys,
      // create seeds). Effective move type, since Move Type is editable.
      const editedMoveType = editedOrderFieldsRef.current.moveType;
      const effMoveType = editedMoveType !== undefined
        ? String(editedMoveType)
        : orderDetails.moveType;
      if (streetTurnLegBlock(effMoveType, orderDetails.reuseContainerPro)) return;
      // Source the visible composition the same way effectiveLegs does, so
      // multiple structural changes compose correctly. legnum on each entry
      // here is its DISPLAY position (1..N); the underlying ordering is
      // captured by the position in the array (insertion order in
      // pendingNewLegs + index in originalLegs).
      const originalLegs = (orderDetails.legs ?? [])
        .slice()
        .sort((a, b) => a.legnum - b.legnum);
      // Same composition as effectiveLegs (see composeLegOrder): order-independent
      // inserts, then removals.
      const composed = composeLegOrder(
        originalLegs, pendingNewLegs, new Set(pendingRemovedLegrecnos),
      );
      // Overlay edits so inheritance reads the user's pending changes.
      const composedWithEdits = composed.map(l => ({
        ...l,
        ...(editedLegs[l.legrecno] || {}),
      }));
      if (composedWithEdits.length === 0) return;

      // A leg added mid-session has to pick up the Default-Send-to-GWI-Move
      // preference the same way every leg present at load did. The effect that
      // stages it runs once per loaded order (keyed on the loaded pronumber) and
      // iterates orderDetails.legs, so it never sees a leg added afterwards.
      //
      // The asymmetry is what bites: assigning a driver cascades across the
      // dispatch scope off effectiveLegs, which DOES include pending-new legs, so
      // the new leg inherits the driver but not the intent to dispatch it. PRO
      // 5127380 — a chassis-split leg inserted ahead of the pickup saved with
      // sendtogwimove='N' while the pickup it was dispatched alongside saved 'Y'.
      // It never reached the tablet, so its origin times were never captured.
      //
      // Same conditions as that effect: preference on, and not a known non-GWI
      // dispatcher (CMS rejects those sends).
      const stageSendToGwiMove =
        Boolean(options?.defaultSendToGwiMove)
        && options?.effectiveDispatcherIsGwi !== false;

      const tempLegrecno = nextTempLegrecno();
      const baseLeg: OrderLeg = {
        legrecno: tempLegrecno,
        sendToGwiMove: stageSendToGwiMove,
        legnum: 0,
        legtype: '',
        origcoordinates: '',
        origcode: 0,
        origApptDate: '',
        origName: '',
        origCityState: '',
        legAction2: '',
        destcoordinates: '',
        destcode: 0,
        destApptDate: '',
        destName: '',
        destCityState: '',
        legDrop: 'N',
        driver: '',
        loadedLeg: 0,
      };

      if (type === 'first') {
        const leg1 = composedWithEdits[0];
        const leg1Drop = leg1.legDrop || 'N';
        // When leg1 is a drop, the inserted leg also lands behind that
        // boundary — timing+driver don't carry, locations still inherit.
        const linkTimes = leg1Drop === 'N';
        const newLeg: OrderLeg = {
          ...baseLeg,
          ...buildDestSideFromOrig(leg1, { linkTimes, includeRequirements: false }),
          legmemo: leg1.origmemo || '',
          legtype: 'CHASSIS SPLIT',
          legAction2: leg1.legtype || 'STOPOFF',
          legDrop: leg1Drop,
          origApptDate: linkTimes ? (leg1.origApptDate || '') : '',
          ...carryDispatchAcrossSplit(leg1, { linkDriver: linkTimes }),
        };
        setPendingNewLegs(prev => [...prev, { leg: newLeg, insert: { type: 'first' } }]);
        return;
      }

      if (type === 'last') {
        const lastLeg = composedWithEdits[composedWithEdits.length - 1];
        const lastAction2 = lastLeg.legAction2 || '';
        const lastLegDrop = lastLeg.legDrop || 'N';
        const linkTimes = lastLegDrop === 'N';
        const newLeg: OrderLeg = {
          ...baseLeg,
          ...buildOrigSideFromDest(lastLeg, { linkTimes, includeRequirements: false }),
          origmemo: lastLeg.legmemo || '',
          legtype: lastAction2,
          legAction2: lastAction2 === 'TERMINATE' ? 'CHASSIS SPLIT' : 'STOPOFF',
          destApptDate: linkTimes ? (lastLeg.destApptDate || '') : '',
          ...carryDispatchAcrossSplit(lastLeg, { linkDriver: linkTimes }),
        };
        setPendingNewLegs(prev => [...prev, { leg: newLeg, insert: { type: 'last' } }]);
        return;
      }

      // type === 'mid' — split the leg with legrecno=target. legrecno is the
      // stable identifier across the renumbering in effectiveLegs; the
      // splitter button passes its `leg.legrecno`.
      const splitLeg = composedWithEdits.find(l => l.legrecno === target);
      if (!splitLeg) return;
      const splitLegDrop = splitLeg.legDrop || 'N';
      const newLeg: OrderLeg = {
        ...baseLeg,
        ...buildDestSideFromDest(splitLeg, { linkTimes: true, includeRequirements: true }),
        legmemo: splitLeg.legmemo || '',
        legtype: 'STOPOFF',
        legAction2: splitLeg.legAction2 || '',
        legDrop: splitLegDrop,
        origApptDate: splitLegDrop === 'N' ? (splitLeg.destApptDate || '') : '',
        ...carryDispatchAcrossSplit(splitLeg, { linkDriver: true }),
      };
      // The split leg's dest side moves onto newLeg; clear the split leg's
      // dest side via editedLegs overlay (mirrors the drawer's
      // clearedSplitLeg construction) and propagate the auto-filled
      // destApptDate = splitLeg.origApptDate.
      const splitClear = clearDestSide();
      setEditedLegs(prev => {
        const updated = { ...prev };
        // Drop any prior dest-side edits on the split leg — they targeted the
        // old dest stop, which has now moved onto newLeg.
        const existing = { ...(updated[splitLeg.legrecno] || {}) };
        DEST_SIDE_FIELDS.forEach(f => {
          delete existing[f];
        });
        Object.assign(existing, splitClear, {
          destApptDate: splitLeg.origApptDate || '',
        });
        updated[splitLeg.legrecno] = existing;
        return updated;
      });
      setPendingNewLegs(prev => [
        ...prev,
        { leg: newLeg, insert: { type: 'mid', afterLegrecno: splitLeg.legrecno } },
      ]);
    },
    [orderDetails, pendingNewLegs, pendingRemovedLegrecnos, editedLegs,
     options?.defaultSendToGwiMove, options?.effectiveDispatcherIsGwi]
  );

  const removeLeg = useCallback(
    (
      legrecno: number,
      removeType: 'origin' | 'destination' = 'origin',
    ): { ok: boolean; reason?: string } => {
      if (!orderDetails) return { ok: false, reason: 'No order loaded' };

      // Use the effective leg list — same view the user sees in the grid.
      // Filters pending-removed and includes pending-new legs, with editedLegs
      // already overlaid per-field. Sourcing merge values from this list means
      // the absorb step picks up the user's CURRENT destination edits, not the
      // raw server snapshot. Also lets the negative-legrecno path participate
      // in the merge instead of bailing early.
      const effLegs = effectiveLegsRef.current;
      const idx = effLegs.findIndex(l => l.legrecno === legrecno);
      if (idx === -1) return { ok: false, reason: 'Leg not found' };
      const realLeg = effLegs[idx];
      const isFirst = idx === 0;
      const isLast = idx === effLegs.length - 1;
      const prevLeg = idx > 0 ? effLegs[idx - 1] : null;
      const isPendingNew = legrecno < 0;

      // Destination removal is only valid on the last leg.
      if (removeType === 'destination' && !isLast) {
        return {
          ok: false,
          reason: 'destination removal is only valid on the last leg',
        };
      }

      // Pending-new legs don't have server-side state to defend (no recorded
      // timestamps, no paid status). Block-checks only apply to real legs.
      if (!isPendingNew) {
        // Block when the side-relevant arrival/depart datetimes are populated.
        // Drawer parity (OrderDetailsModalContent.tsx ~444-456):
        //   destination on last leg: this leg's dest arrival/depart
        //   origin on first leg:     this leg's orig arrival/depart
        //   origin on interior:      prev leg's dest arrival/depart + this leg's orig arrival/depart
        const blockers: string[] = [];
        const pushIf = (leg: OrderLeg, field: keyof OrderLeg, label: string) => {
          // realLeg + prevLeg here come from effectiveLegs, already overlaid.
          if (String(leg[field] ?? '').trim()) blockers.push(label);
        };
        if (removeType === 'destination' && isLast) {
          pushIf(realLeg, 'destArrivalDateTime', `Leg ${realLeg.legnum} destination arrival`);
          pushIf(realLeg, 'destDepartDateTime', `Leg ${realLeg.legnum} destination depart`);
        } else if (isFirst) {
          pushIf(realLeg, 'origArrivalDateTime', `Leg ${realLeg.legnum} origin arrival`);
          pushIf(realLeg, 'origDepartDateTime', `Leg ${realLeg.legnum} origin depart`);
        } else if (prevLeg) {
          pushIf(prevLeg, 'destArrivalDateTime', `Leg ${prevLeg.legnum} destination arrival`);
          pushIf(prevLeg, 'destDepartDateTime', `Leg ${prevLeg.legnum} destination depart`);
          pushIf(realLeg, 'origArrivalDateTime', `Leg ${realLeg.legnum} origin arrival`);
          pushIf(realLeg, 'origDepartDateTime', `Leg ${realLeg.legnum} origin depart`);
        }
        if (blockers.length > 0) {
          return {
            ok: false,
            reason: `Cannot delete stop: clear ${blockers.join(', ')} first`,
          };
        }

        // Block paid legs — paid status is server-recorded and removal would
        // orphan the payment record. UI hides the button via the relevant guards;
        // this is hook-level defense in depth.
        if (realLeg.isPaid === true) {
          return {
            ok: false,
            reason: `Cannot delete leg ${realLeg.legnum}: leg is paid`,
          };
        }

        // Block removing a DELIVERY destination outright — the loaded->empty
        // boundary anchors the order's delivery stop.
        if (removeType === 'destination' && realLeg.legAction2 === 'DELIVERY') {
          return {
            ok: false,
            reason: `Cannot delete leg ${realLeg.legnum}: DELIVERY destinations cannot be removed`,
          };
        }
      }

      // Interior origin removal absorbs this leg's destination into prev — runs
      // for both real and pending-new legs.
      const isInteriorMerge =
        removeType === 'origin' && !isFirst && prevLeg !== null;

      // For an interior origin removal where prev is a real paid leg, block —
      // mutating paid leg data isn't allowed.
      if (isInteriorMerge && prevLeg!.isPaid === true) {
        return {
          ok: false,
          reason: `Cannot delete leg ${realLeg.legnum}: previous leg ${prevLeg!.legnum} is paid`,
        };
      }

      // For interior origin removal the boundary being destroyed is the shared
      // stop between prev and this leg (prev.legAction2 = this.legtype). If
      // that boundary was DELIVERY, removing it deletes the order's delivery
      // anchor — block.
      if (
        isInteriorMerge &&
        prevLeg &&
        prevLeg.legAction2 === 'DELIVERY'
      ) {
        return {
          ok: false,
          reason: `Cannot delete leg ${realLeg.legnum}: previous leg ${prevLeg.legnum} is a DELIVERY`,
        };
      }

      // Apply the merge for interior-origin removal: write DEST_MERGE_FIELDS
      // from this leg's EFFECTIVE values onto the prev leg's edits overlay so
      // the merged-prev shape reflects any unsaved edits to this leg's dest.
      setEditedLegs(prev => {
        const updated = { ...prev };
        if (isInteriorMerge && prevLeg) {
          // realLeg comes from effectiveLegs — already has editedLegs overlaid,
          // so overlayFromLeg picks up the user's current dest values.
          const mergeOverlay = overlayFromLeg(realLeg, DEST_MERGE_FIELDS);
          const prevEdits = updated[prevLeg.legrecno] ?? {};
          // Drop prev's stale dest-side edits (they referenced the now-gone
          // shared stop) before layering the merge values on top.
          const cleaned: Partial<OrderLeg> = { ...prevEdits };
          for (const f of DEST_SIDE_FIELDS) delete cleaned[f];
          updated[prevLeg.legrecno] = { ...cleaned, ...mergeOverlay };
        }
        // Drop edits for the removed leg — they no longer apply.
        if (updated[legrecno]) delete updated[legrecno];
        return updated;
      });

      if (isPendingNew) {
        // Drop the pending entry (never persisted, so no removed-marker needed) AND
        // re-anchor any pending-new leg that was inserted right after this one. Its
        // `afterLegrecno` points at this (now-gone) pending leg, so without
        // re-anchoring the anchor vanishes entirely from the composition and the
        // dependent leg falls to the end — flipping the on-screen order (e.g. add
        // two chained stopoffs, delete the first). Re-point onto this leg's
        // predecessor so the dependent leg keeps its slot; null predecessor (this
        // was the first leg) becomes a 'first' insert. A removed REAL leg needs no
        // re-anchor: it stays in originalLegs, so effectiveLegs resolves the anchor
        // during the insert pass and only then filters it out.
        setPendingNewLegs(prev =>
          prev
            .filter(e => e.leg.legrecno !== legrecno)
            .map(e =>
              e.insert.type === 'mid' && e.insert.afterLegrecno === legrecno
                ? {
                    ...e,
                    insert: prevLeg
                      ? { type: 'mid' as const, afterLegrecno: prevLeg.legrecno }
                      : { type: 'first' as const },
                  }
                : e,
            ),
        );
      } else {
        setPendingRemovedLegrecnos(prev =>
          prev.includes(legrecno) ? prev : [...prev, legrecno]
        );
      }
      return { ok: true };
    },
    [orderDetails, editedLegs, pendingRemovedLegrecnos]
  );

  const restoreLeg = useCallback((legrecno: number) => {
    setPendingRemovedLegrecnos(prev => prev.filter(l => l !== legrecno));
  }, []);

  // Port of the drawer's `executeClearDriver` (OrderDetailsModalContent.tsx:2114-2131).
  // Clears `driver` across the dispatch scope and (optionally) nulls the four
  // arrival/depart datetime fields. Reads scope/orderDetails via refs so the
  // callback identity stays stable.
  const clearDriver = useCallback((legrecno: number, options?: { clearDates?: boolean }) => {
    const orderDetails = orderDetailsRef.current;
    if (!orderDetails) return;
    const effLegs = effectiveLegsRef.current;
    const scope = getDispatchScope(legrecno, effLegs);
    const dateFields: (keyof OrderLeg)[] = [
      'origArrivalDateTime',
      'origDepartDateTime',
      'destArrivalDateTime',
      'destDepartDateTime',
    ];

    setEditedLegs(prev => {
      const next = { ...prev };
      for (const scopeLegrecno of scope) {
        const existing = next[scopeLegrecno] || {};
        const patch: Partial<OrderLeg> = { ...existing, driver: '' };
        if (options?.clearDates) {
          for (const f of dateFields) (patch as any)[f] = null;
        }
        next[scopeLegrecno] = patch;
      }
      return next;
    });
  }, []);

  // Clear ONLY the appointment dates (origApptDate / destApptDate) on the given
  // legs — appointment TIMES are left intact (they're location-specific loading
  // times, not tied to the schedule date). '' is the established cleared value
  // the save payload already maps to the V1 zero-date.
  const clearLegApptDates = useCallback((legrecnos: number[]) => {
    if (!legrecnos.length) return;
    setEditedLegs(prev => {
      const next = { ...prev };
      for (const legrecno of legrecnos) {
        next[legrecno] = { ...(next[legrecno] || {}), origApptDate: '', destApptDate: '' };
      }
      return next;
    });
  }, []);

  const dismissDropApptClearPrompt = useCallback(() => setDropApptClearPrompt(null), []);

  // Confirm ("Clear Appt Dates"): clear the dates on the prompted legs, then
  // close. Reads the prompt from a ref to avoid a stale closure and to keep off
  // the setState-inside-updater path.
  const confirmDropApptClear = useCallback(() => {
    const prompt = dropApptClearPromptRef.current;
    if (prompt?.kind === 'confirm') clearLegApptDates(prompt.legrecnos);
    setDropApptClearPrompt(null);
  }, [clearLegApptDates]);

  // Validate all changes before saving. Per-leg rules live in the shared
  // `validateLegChanges` helper (also called by OrderDetailsModalContent),
  // so the drawer and the /pro page enforce the same constraints.
  //
  // Uses effectiveLegs (not orderDetails.legs) so pendingNewLegs are
  // validated and pendingRemovedLegrecnos are excluded — without this,
  // adding a second DELIVERY leg via "+ Add Leg" would slip past the
  // uniqueness check. effectiveLegs already overlays editedLegs, so the
  // shared helper gets an empty edits map to avoid double-overlay.
  const validateChanges = useCallback((): string[] => {
    if (!orderDetails) return [];
    const otr = orderDetails.otr === 'Y';

    // Move Type is EDITABLE (order editor + the create modal's seed), so every
    // movetype-conditional rule below has to read the effective value — an edit
    // else the saved one. Reading orderDetails.moveType alone gated on a stale
    // value: switching an IMPORT to INBOUND kept demanding a container, and the
    // reverse never started demanding one. The backend already merges this the
    // same way (order_create_service._effective_order_value).
    const effMoveType = String(
      editedOrderFields.moveType !== undefined
        ? editedOrderFields.moveType
        : orderDetails.moveType ?? '',
    ).trim();

    const errors: string[] = validateLegChanges({
      legs: effectiveLegs,
      editedLegs: {},
      otr,
    });

    // --- Order-level validations ---

    // Container-required is an order-completeness check. A template is a blueprint
    // that legitimately saves without a container (it's assigned when a real order
    // is created from it), so skip this requirement for templates.
    if (!options?.isTemplate) {
      const effectiveContainer = editedOrderFields.container !== undefined
        ? editedOrderFields.container
        : orderDetails.container;
      const containerRequiredError = validateContainerRequired(
        effMoveType,
        effectiveContainer,
      );
      if (containerRequiredError) errors.push(containerRequiredError);
    }

    // Whse-entry paperwork gate (V1 lib_editrecord): block the save if the pickup
    // (originate) leg's orig appt date is being set to a non-blank value while a
    // warehouse entry number is present but the 3461 date is not.
    const effWhseEntry = editedOrderFields.whseEntryNum !== undefined
      ? editedOrderFields.whseEntryNum : orderDetails.whseEntryNum;
    const effWhse3461 = editedOrderFields.whse3461Date !== undefined
      ? editedOrderFields.whse3461Date : orderDetails.whse3461Date;
    if (String(effWhseEntry ?? '').trim() !== '' && String(effWhse3461 ?? '').trim() === '') {
      const origLegNum = orderDetails.originateLeg;
      const effLeg = effectiveLegs.find((l) => l.legnum === origLegNum);
      const savedLeg = orderDetails.legs?.find((l) => l.legnum === origLegNum);
      const newAppt = String(effLeg?.origApptDate ?? '').trim();
      const oldAppt = String(savedLeg?.origApptDate ?? '').trim();
      if (newAppt !== '' && newAppt !== oldAppt) {
        errors.push('Required paperwork for warehouse entry not received.');
      }
    }

    // IDO # (freightowner) vs pickup driver mutual exclusion (V1): on an IMPORT
    // you can't have both an originate-leg pickup driver AND an IDO #. Block
    //
    // KEPT even though 360 no longer offers the field (migration 488 closed it; the
    // form input is gone). V1 still shows and can still set it, so a value can still
    // arrive — and V1's own cms.js:5317-5325 would refuse the save anyway. Half of this
    // is now unreachable from 360 (nothing can add an IDO # here), and the other half
    // only fires on an order already carrying one, of which prod has zero live. Dropping
    // it would be a silent divergence rather than a cleanup.
    // whichever was just added — driver just assigned → the IDO-# message,
    // IDO # just added → the driver message.
    if (effMoveType === 'IMPORT') {
      const origLegNum = orderDetails.originateLeg;
      const effOrigLeg = effectiveLegs.find((l) => l.legnum === origLegNum);
      const savedOrigLeg = orderDetails.legs?.find((l) => l.legnum === origLegNum);
      const newDriver = String(effOrigLeg?.driver ?? '').trim();
      const oldDriver = String(savedOrigLeg?.driver ?? '').trim();
      const newIdo = String(
        editedOrderFields.idoNumber !== undefined
          ? editedOrderFields.idoNumber
          : orderDetails.idoNumber ?? '',
      ).trim();
      const oldIdo = String(orderDetails.idoNumber ?? '').trim();
      if (newDriver !== '' && newIdo !== '') {
        if (oldDriver === '') {
          errors.push('Pickup driver cannot be assigned if IDO # is added.');
        } else if (oldIdo === '') {
          errors.push('IDO # cannot be added if pickup driver is assigned.');
        }
      }
    }

    // Inbond pending-approval gate (V1 lib_editrecord): Pending Approval can't be
    // set to RESOLVED (from a non-RESOLVED state) while an inbond move is
    // unresolved — inbond type set, tender not received, and responsibility not
    // OTHER. Inert today (pendingApproval is read-only), it activates once that
    // field is editable through the pro-level save (editedOrderFields.pendingApproval);
    // the current value is read from invoiceMain otherwise.
    const oldPending = String(orderDetails.invoiceMain?.pendingApproval ?? '');
    const newPending = editedOrderFields.pendingApproval !== undefined
      ? String(editedOrderFields.pendingApproval) : oldPending;
    if (newPending === 'RESOLVED' && oldPending !== 'RESOLVED') {
      const effInbondType = editedOrderFields.inbondType !== undefined
        ? editedOrderFields.inbondType : orderDetails.inbondType;
      const effTender = editedOrderFields.inbondTenderRcvd !== undefined
        ? editedOrderFields.inbondTenderRcvd : orderDetails.inbondTenderRcvd;
      const effResp = editedOrderFields.inbondResponsible !== undefined
        ? editedOrderFields.inbondResponsible : orderDetails.inbondResponsible;
      if (
        String(effInbondType ?? '').trim() !== '' &&
        String(effTender ?? '') !== 'Y' &&
        String(effResp ?? '') !== 'OTHER'
      ) {
        errors.push('Pending Approval cannot be RESOLVED until Inbond Tender Received.');
      }

      // Field-9 Pending-Approval gate (V1 edit_savejs invoicing block): every
      // invoice line item must be fully entered (Item + GL Code + Qty≠0 + Rate≠0)
      // or fully blank — no half-filled rows. Kay Poly (8921) / Vinmar (1902)
      // FUEL SURCHARGE lines are exempt. Rows come from the tab's live draft
      // (mirrored into the hook) so the check sees unsaved edits; fall back to the
      // saved line items when the tab was never opened.
      const num = (v: unknown): number => {
        const n = parseFloat(String(v ?? ''));
        return Number.isNaN(n) ? 0 : n;
      };
      const billto = Number(orderDetails.billTo) || 0;
      const liRows: Array<{ item?: string; glCode?: string; qty?: unknown; rate?: unknown }> =
        invoiceLineItemsRef.current ?? (orderDetails.invoiceLineItems ?? []);
      for (const r of liRows) {
        const item = String(r.item ?? '').trim();
        const gl = String(r.glCode ?? '').trim();
        const qty = num(r.qty);
        const rate = num(r.rate);
        const fuelException =
          (billto === 8921 || billto === 1902) && item === 'FUEL SURCHARGE';
        const allEmpty = item === '' && gl === '' && qty === 0 && rate === 0;
        const allFilled = item !== '' && gl !== '' && qty !== 0 && rate !== 0;
        if (!(allEmpty || allFilled || fuelException)) {
          errors.push('Following invoice line items must be entered Item, GL Code, Qty, Rate');
          break;
        }
      }

      // Whse-tab warehouse gate (Dawn's ticket 2026-10-14, direction from Lee): a
      // GWI-coded line must name a warehouse the pro worked through — the same rows
      // the Whse tab shows, handed over as `whseCodes`. Same live-draft-else-saved
      // rows as the gate above; the server re-checks the persisted lines in
      // validate_pending_approval, so recoding and resolving in one Save works
      // (the line batch lands before the header in every mode).
      //
      // Only the dray items block (WHSE_BLOCKING_ITEMS, Lee 2026-10-16) — the tab still
      // paints EVERY GWI-coded miss red on load from the same module, which is the
      // callout V1 had. Items like GROUNDED / FUEL SURCHARGE carry a warehouse code the
      // move never touched often enough that blocking on them stopped legitimate saves.
      errors.push(...whseGlCodeErrors(liRows, orderDetails.whseCodes));

      // Per Diem Responsible required when there's a live per-diem amount
      // (V1 INC-12161): `perdiemapproval` here is the responsible-party field on
      // the Addl Cost tab, not the require-per-diem-approval flag.
      const pdAmount = num(orderDetails.perDiemAmount);
      const effPdResp = editedOrderFields.perDiemApproval !== undefined
        ? editedOrderFields.perDiemApproval
        : orderDetails.perDiemApproval;
      if (pdAmount !== 0 && String(effPdResp ?? '').trim() === '') {
        errors.push('Per Diem Responsible is required to update Pending Approval');
      }
    }

    // Invoice hold pair (V1 lib_editrecord.pl:1446-1457): a hold reason requires
    // a last follow-up, and an existing follow-up can't be cleared. Moved here
    // from the Invoicing tab's own save now that the tab commits through the
    // order-level save. GATED on the hold fields actually being EDITED — otherwise
    // an order with legacy data (a saved hold reason but a NULL/0 follow-up) would
    // block EVERY save (e.g. a dispatcher editing a leg), which the tab-scoped
    // version never did.
    if (
      editedOrderFields.invoiceHoldReason !== undefined ||
      editedOrderFields.invoiceHoldFollowupDatetime !== undefined
    ) {
      const savedFollowup = String(orderDetails.invoiceHoldFollowupDatetime ?? '');
      const effReason = editedOrderFields.invoiceHoldReason !== undefined
        ? String(editedOrderFields.invoiceHoldReason)
        : String(orderDetails.invoiceHoldReason ?? '');
      const effFollowup = editedOrderFields.invoiceHoldFollowupDatetime !== undefined
        ? String(editedOrderFields.invoiceHoldFollowupDatetime)
        : savedFollowup;
      if (effReason.trim() !== '' && effFollowup.trim() === '') {
        errors.push('Last Follow Up is required when a Hold Reason is selected. Please update.');
      } else if (savedFollowup.trim() !== '' && effFollowup.trim() === '') {
        errors.push('Last Follow Up cannot be cleared once it has been set. Please update.');
      }
    }

    // Revised DO = Y requires a Hold Reason. UNLIKE the hold pair above this is NOT
    // gated on the fields being edited — every save of an order holding a Y has to
    // carry a reason, so a legacy row can't keep slipping through on unrelated saves
    // (Lee, 2026-09-25; 32 live orders are in that state today and will each need a
    // reason before they save again).
    //
    // It IS gated on the user being able to edit the pair (`canEditInvoiceHold`, the
    // order-lc-fields grant that drives InvoicingView's `holdEditable`). Both halves
    // are read-only for a dispatcher — GWI360-Dispatch and GWI360-DispatchAssistant
    // are View on that component — so leaving it ungated told them to fill in a field
    // they cannot see or reach, with no way out of their own save. Same precedent as
    // the ports gate below and hazmat Rule 6 in validateOrderSave: a save-block fires
    // only for the audience holding write access to the field it names. The
    // unconditional half is unchanged for everyone who CAN fix it (LCs, LC managers,
    // invoice clerks, dispatch managers, developers, global admins).
    //
    // Only the MAIN invoice's flag is visible here — orderDetails carries invoiceMain,
    // while accessorial invoices load separately — so the server-side twin
    // (_revised_do_hold_error_on_save) is what covers a Y sitting on an accessorial.
    // Templates are exempt, matching invoice_hold_rules server-side ("a template is
    // a blueprint and is routinely, legitimately incomplete"). Without this a
    // template could be refused in the browser by a rule the backend says does not
    // apply to it — /pro serves the Invoicing tab on templates too.
    if (!options?.isTemplate && options?.canEditInvoiceHold) {
      const effRevisedDo = editedOrderFields.requireRevisedDo !== undefined
        ? editedOrderFields.requireRevisedDo
        : orderDetails.invoiceMain?.requireRevisedDo;
      const effHoldReason = editedOrderFields.invoiceHoldReason !== undefined
        ? editedOrderFields.invoiceHoldReason
        : orderDetails.invoiceHoldReason;
      if (String(effRevisedDo ?? '').trim().toUpperCase() === 'Y'
          && String(effHoldReason ?? '').trim() === '') {
        errors.push('Hold Reason is required when Invoice Hold is Y. Please update.');
      }
    }

    // Street Turn Review gate (V1): no driver may be on the terminate leg while
    // Street Turn Review (the `mule` column, surfaced as streetTurnReview) is Y.
    const effMule = editedOrderFields.streetTurnReview !== undefined
      ? editedOrderFields.streetTurnReview : orderDetails.streetTurnReview;
    if (String(effMule ?? '') === 'Y') {
      const termLeg = effectiveLegs.find((l) => l.legnum === orderDetails.terminateLeg);
      if (String(termLeg?.driver ?? '').trim() !== '') {
        errors.push('Cannot add driver to terminate leg because Street Turn Review is Y.');
      }
    }

    // Import GTG → ports gate (V1 Tracing save): on an import, once the
    // Available Good-To-Go datetime is set, both Port of Origin and Port of
    // Discharge are required — but only for users who can edit the ports
    // fields (legacy only blocked those users). The ports columns store the
    // cms.ports recno, so a set value is a non-zero code.
    if (options?.canEditPorts && effMoveType === 'IMPORT') {
      const effGtg = editedOrderFields.availableGoodToGoDatetime !== undefined
        ? editedOrderFields.availableGoodToGoDatetime
        : orderDetails.availableGoodToGoDatetime;
      if (String(effGtg ?? '').trim() !== '') {
        const effPoo = editedOrderFields.portOfOriginCode !== undefined
          ? editedOrderFields.portOfOriginCode : orderDetails.portOfOriginCode;
        const effPod = editedOrderFields.portOfDischargeCode !== undefined
          ? editedOrderFields.portOfDischargeCode : orderDetails.portOfDischargeCode;
        if (!Number(effPoo) || !Number(effPod)) {
          errors.push('Port of Origin and Port of Discharge are required when Available/GTG is set.');
        }
      }
    }

    // Cutoff-time gate (V1 lib_editrecord.pl 1367): a *changed* cutoff datetime
    // must carry a real time — a 00:00 time means the user picked a date but
    // never entered a time. Only fires on edit (matches legacy's old-vs-new
    // compare); blank (cleared) is allowed. Edited values are 'MM/DD/YYYY HH:MM'.
    if (editedOrderFields.cutoffDatetime !== undefined) {
      const v = String(editedOrderFields.cutoffDatetime).trim();
      const timePart = v.split(/\s+/)[1] ?? '';
      if (v !== '' && timePart.slice(0, 5) === '00:00') {
        errors.push('Time is required for cutoff.');
      }
    }

    // Required-field chain (V1 lib_editrecord.pl 1401-1494). Orders only —
    // templates run their own approve/edit gate. /pro edits EXISTING orders, so
    // for the universal fields (all populated on any real order) this bites only
    // when a user CLEARS one; a read-only-on-that-field user never clears it, so
    // we don't need the legacy per-field write-ACL gate. The specialized fields
    // (rate zone / masterpro / region) use explicit can't-clear semantics so
    // orders legitimately saved blank aren't retroactively blocked — Region
    // matches legacy exactly (it only required a region when one was already
    // set). The legacy new-order-only broker / bill-to-contact checks are
    // omitted: /pro has no new-order-creation flow.
    if (!options?.isTemplate) {
      const reqVal = (key: string): string => {
        const v = editedOrderFields[key] !== undefined
          ? editedOrderFields[key] : (orderDetails as any)[key];
        return v == null ? '' : String(v).trim();
      };
      const savedVal = (key: string): string => {
        const v = (orderDetails as any)[key];
        return v == null ? '' : String(v).trim();
      };

      if (reqVal('csr') === '') errors.push('CSR is required.');
      if (reqVal('dispatcher') === '') errors.push('Dispatcher is required.');
      if (reqVal('customerName') === '') errors.push('Customer is required.');
      if (reqVal('billToName') === '') errors.push('Bill To is required.');

      if (effMoveType === 'IMPORT' || effMoveType === 'EXPORT' || effMoveType === 'RAIL TO PORT') {
        if (reqVal('rail') === '') errors.push('Terminal is required.');
        if (reqVal('traceMode') === '') errors.push('Mode is required.');
      }

      // Quote # (the invoice header's v1-quote link) — required only when the
      // invoice-quote-number component resolves to exactly Edit for this user
      // (LCs); Exempt and un-granted users can edit without the requirement.
      // Anyone blocked here can always fix it inline — Edit implies the field
      // is open on their Invoicing tab. 0 is V1's "no quote" sentinel; the
      // saved value rides invoiceMain, edits ride editedOrderFields like the
      // other header fields.
      // ...and only on an invoice actually billed from CMS. Scoped to match the
      // invoice-side quote validation, which exempts non-CMS rows for the same reason:
      // a DO NOT BILL pro is not going to be invoiced from here, so demanding a quote
      // on it blocks work for nothing. On prod that is the difference between 172
      // active orders and 1,194.
      if (options?.quoteNumberRequired) {
        const main = (orderDetails as any).invoiceMain;
        const effQuote = editedOrderFields.quoteNumber !== undefined
          ? editedOrderFields.quoteNumber
          : main?.quoteNumber;
        const effBillFrom = editedOrderFields.billFromSystem !== undefined
          ? editedOrderFields.billFromSystem
          : main?.billFromSystem;
        const billedFromCms = String(effBillFrom ?? '').trim().toUpperCase() === 'CMS';
        if (billedFromCms && !Number(effQuote)) errors.push('Quote # is required.');
      }

      // Weight is required on IMPORT orders — for everyone (the server mirrors
      // it in save_order_details / validate_create_payload). `lbs` is the
      // authoritative weight (kgs / metric tons cascade into it) and the details
      // response coerces a NULL lbs to 0, so 0 and blank both count as missing.
      // Edits ride editedOrderFields like the other header fields.
      const effLbs = editedOrderFields.lbs !== undefined
        ? editedOrderFields.lbs
        : (orderDetails as any).lbs;
      if (isImportMissingWeight(effMoveType, effLbs)) {
        errors.push(importWeightRequiredMessage(options?.canEditWeightFields));
      }

      // Can't-clear: only enforced when the saved order already carries a value.
      if (savedVal('templateType') !== '' && reqVal('templateType') === '')
        errors.push('Pickup Rate Zone is required.');
      if (savedVal('masterPro') !== '' && reqVal('masterPro') === '')
        errors.push('Masterpro is required.');
      if (savedVal('revenueRegion') !== '' && reqVal('revenueRegion') === '')
        errors.push('Region is required.');
    }

    // Templates: Pickup Rate Zone is required on EVERY save, not merely
    // can't-clear (#33468). V1's guard — cms_chkempty(order_templatetype,
    // 'Pickup Rate Zone'), lib_editrecord.pl:1474 — was NOT gated on istemplate,
    // which is why 0 of 1,845 legacy-created 2026 templates carry a blank rate
    // zone. The required-field chain above is orders-only ("templates run their
    // own approve/edit gate"), and nothing took its place, so a skeleton could be
    // saved AND approved with no rate zone: 8 of the first 10 templates created
    // through POST /templates were. A blank rate zone quietly breaks the quote
    // spyglass, which needs it to match a DIRECT quote (quotes.lookup-for-pro).
    //
    // Gated on the field being editable — our equivalent of V1's
    // `getACL('templatetype') eq 'w'`. templateType is a State-2 template field,
    // so it LOCKS on approval; without this gate an ALREADY-approved template with
    // a blank rate zone could never be saved again, because nobody can fill the
    // field in. The template-edit-fields permission needs no separate check:
    // without it nothing on the template is editable, so there is no save to block.
    // Mode is required on the port move types for a TEMPLATE as well as an order —
    // v1 lib_editrecord.pl:1461-1468, where `order_rail` (Terminal) sits behind
    // `if(!$istemplate)` but `order_mode` deliberately does not: "mode on templates
    // and orders". Our orders-only chain above therefore left templates short.
    // CSR (the LC) is required on a template, exactly as on an order — v1's
    // cms_chkempty(order_csr, 'CSR') at lib_editrecord.pl:1440 is NOT inside the
    // `if(!$istemplate)` that follows it, which gates Dispatcher and Masterpro.
    // Our required-field chain is orders-only, so nothing enforced it here and the
    // only thing stopping a csr-less template was native_save's server-side gate —
    // a 422 after the fact instead of the inline banner every other required field
    // gets. A created template with no LC also cannot be saved again until one is
    // added, which is how this surfaced.
    if (options?.isTemplate) {
      const effCsr = editedOrderFields.csr !== undefined
        ? editedOrderFields.csr
        : (orderDetails as any).csr;
      if (String(effCsr ?? '').trim() === '') errors.push('CSR is required.');
    }

    if (options?.isTemplate
        && ['IMPORT', 'EXPORT', 'RAIL TO PORT'].includes(effMoveType)) {
      const effMode = editedOrderFields.traceMode !== undefined
        ? editedOrderFields.traceMode
        : (orderDetails as any).traceMode;
      if (String(effMode ?? '').trim() === '') errors.push('Mode is required.');
    }

    if (options?.isTemplate) {
      const effRateZone = editedOrderFields.templateType !== undefined
        ? editedOrderFields.templateType
        : (orderDetails as any).templateType;
      const rateZoneEditable = templateFieldEditable('templateType', {
        isApproved: isTemplateApproved(orderDetails),
      });
      if (rateZoneEditable && String(effRateZone ?? '').trim() === '') {
        errors.push('Pickup Rate Zone is required.');
      }
    }

    // Create-only rules (V1's new-order requirements, deliberately omitted on
    // the edit path above — see the "no new-order-creation flow" note). Kept in
    // the same funnel so the SaveBar banner renders them like any other error.
    if (createActiveRef.current) {
      const effVal = (key: string): string => {
        const v = editedOrderFields[key] !== undefined
          ? editedOrderFields[key] : (orderDetails as any)[key];
        return v == null ? '' : String(v).trim();
      };
      // Broker/contact NAMES are read-derived (JOIN) while the writable column
      // is the FK recno: a freshly picked value stages both, a seeded one may
      // carry only one side. Either counts as "set".
      const nameOrCode = (nameKey: string, codeKey: string): string =>
        effVal(nameKey) || (Number(effVal(codeKey)) ? effVal(codeKey) : '');
      // ORDERS only. A Delivery Order is paperwork for a real move; a template is
      // a blueprint and its docs are copied by reference when an order is created
      // FROM it, so requiring one to save the blueprint would be backwards.
      // Effective move type, not the seed — DOMESTIC is exempt and the user can
      // switch move type after the create modal.
      if (!options?.isTemplate) {
        const docError = deliveryOrderDocError(
          createModeRef.current?.stagedDocTypes ?? [],
          createModeRef.current?.templateDocTypes ?? null,
          effMoveType,
        );
        if (docError) errors.push(docError);
      }

      errors.push(
        ...createOrderValidationErrors({
          isTemplate: !!options?.isTemplate,
          moveType: effMoveType,
          rateZone: effVal('templateType'), // "Pickup Rate Zone" = ordersall.templatetype
          brokerName: nameOrCode('brokerName', 'brokerCode'),
          brokerContactName: nameOrCode('brokerContactName', 'brokerContactRecno'),
          billToContactName: nameOrCode('billToContactName', 'billToContactRecno'),
          legs: effectiveLegs.map((l) => ({
            legtype: String(l.legtype ?? ''),
            legAction2: String(l.legAction2 ?? ''),
          })),
        }),
      );
    }

    // Re-run chassis-type compatibility checks whenever any of the relevant
    // fields was edited (chassisType, size, equipType, chassisProvider). Uses
    // effective values so the check catches edits that flow in from any of
    // those fields. Pre-existing bad data isn't flagged unless the user
    // touched one of these fields.
    const chassisRelevantEdit =
      editedOrderFields.chassisType !== undefined ||
      editedOrderFields.size !== undefined ||
      editedOrderFields.equipType !== undefined ||
      editedOrderFields.chassisProvider !== undefined ||
      editedOrderFields.isTank !== undefined;
    if (chassisRelevantEdit) {
      const effectiveCt = editedOrderFields.chassisType !== undefined
        ? editedOrderFields.chassisType
        : (orderDetails.chassisType || '');
      const effectiveSize = editedOrderFields.size !== undefined
        ? editedOrderFields.size
        : (orderDetails.size || '');
      const effectiveEquipType = editedOrderFields.equipType !== undefined
        ? editedOrderFields.equipType
        : (orderDetails.equipType || '');
      const effectiveProvider = editedOrderFields.chassisProvider !== undefined
        ? editedOrderFields.chassisProvider
        : (orderDetails.chassisProvider || '');
      const effectiveIsTank = editedOrderFields.isTank !== undefined
        ? editedOrderFields.isTank
        : (orderDetails.isTank || '');
      const chassisErrs = validateChassisCompatibility(effectiveCt, effectiveSize, effectiveEquipType, effectiveProvider, effectiveIsTank, chassisMatrix, { isTemplate: !!options?.isTemplate });
      errors.push(...chassisErrs.map((e) => e.message));
    }

    // V1 parity hard-block validations (lib_editrecord.pl 1000-1700). Same
    // function the OrderFormView banner renders — so what users see inline
    // matches what blocks the save.
    errors.push(...validateOrderSave(editedOrderFields, orderDetails, {
      isTemplate: !!options?.isTemplate,
      canEditHazmat: options?.canEditHazmat,
      // Loaded-leg hazmat gate needs leg data — compute from the effective
      // (edited-overlaid) legs so an in-session driver assignment counts.
      hazmatLegActive: hazmatInfoLegActive(orderDetails, effectiveLegs),
    }));

    // Async-computed errors injected via setExtraSaveErrors, keyed by
    // source so multiple contributors coexist. Synchronous read from the
    // ref so timing of the latest write doesn't matter.
    for (const sourceErrors of extraSaveErrorsRef.current.values()) {
      errors.push(...sourceErrors);
    }

    return errors;
  }, [orderDetails, effectiveLegs, editedOrderFields, chassisMatrix, options?.canEditHazmat, options?.canEditPorts, options?.canEditInvoiceHold, options?.quoteNumberRequired, options?.canEditWeightFields, options?.isTemplate]);

  const save = useCallback(
    async (
      options: {
        deadlineNote?: string;
        excludeLegrecnos?: number[];
        deletereason?: string;
        /** Transient prompt inputs the saverules engine reads via ctx.input()
         *  (V1 $qpst->param) — currently the rolling modal's rollingreason /
         *  rollingissue / missedcutoffyesno. Not persisted columns. */
        inputs?: Record<string, string>;
        confirmDuplicate?: (dupes: Array<string | number>) => Promise<boolean>;
        confirmMissingBillToRef?: () => Promise<boolean>;
        confirmCreateWarnings?: (warnings: string[]) => Promise<boolean>;
      } = {}
    ): Promise<SaveResult> => {
      if (!orderDetails) return { ok: false, type: 'no-changes' };
      // NOTE: the hook's own `options` (createMode et al.) is SHADOWED by this
      // callback's parameter — read create mode through the ref.
      const createMode = createActiveRef.current ? createModeRef.current : null;
      const hasChangesNow =
        Object.keys(editedOrderFields).length > 0 ||
        Object.keys(editedLegs).length > 0 ||
        pendingNewLegs.length > 0 ||
        pendingRemovedLegrecnos.length > 0 ||
        invoiceDirty ||
        // A staged Invoice flag is a real change: without this a save whose
        // ONLY edit is a Docs-tab flag would take the no-changes early-out
        // and silently discard it.
        Object.keys(docInvoiceFlagsRef.current).length > 0;
      // A create with zero drawer edits is still a save — only the edit path
      // has a no-changes early-out.
      if (!hasChangesNow && !createMode) return { ok: false, type: 'no-changes' };

      // Bail if a save is already running (see savingRef). Returning 'aborted'
      // keeps callers on their existing no-op path for a superseded save, so a
      // duplicate click never fires a second request. Set the lock synchronously
      // — before any await — so the window between click #1 and the isSaving
      // re-render can't let click #2 through.
      if (savingRef.current) return { ok: false, type: 'aborted' };
      savingRef.current = true;

      // Flip isSaving on immediately so the Save button shows the spinner /
      // "Saving…" state as soon as the user clicks, regardless of whether
      // validation passes or the network round-trips are fast. The finally
      // block at the bottom clears it on every exit path.
      if (mountedRef.current) {
        setIsSaving(true);
        setSaveError(null);
      }

      const errors = validateChanges();
      if (errors.length > 0) {
        savingRef.current = false;
        if (mountedRef.current) {
          setSaveError(errors.join('\n'));
          // Latch the banner on. Unlike `saveError` (cleared by the edit effect
          // above so a stale message can't masquerade as a fresh failure), this
          // STAYS set so the form can keep re-running validateChanges as the
          // user fixes each item — errors drop off the list live instead of
          // needing another Save/Create click to re-evaluate.
          setValidationAttempted(true);
          setIsSaving(false);
        }
        return { ok: false, type: 'validation', errors };
      }

      // V1 field-9 SOFT gate (edit_savejs invoicing block): setting Pending
      // Approval to RESOLVED with a blank Bill To Ref# prompts a confirm (not a
      // hard block). The caller supplies the shared modal via confirmMissingBillToRef;
      // declining aborts the save. Runs before the write, after the hard validations.
      {
        const oldPending = String(orderDetails.invoiceMain?.pendingApproval ?? '');
        const newPending = editedOrderFields.pendingApproval !== undefined
          ? String(editedOrderFields.pendingApproval) : oldPending;
        if (newPending === 'RESOLVED' && oldPending !== 'RESOLVED') {
          const effRef = editedOrderFields.billToRefNo !== undefined
            ? editedOrderFields.billToRefNo
            : orderDetails.invoiceMain?.billToRefNo;
          if (String(effRef ?? '').trim() === '' && options.confirmMissingBillToRef) {
            const proceed = await options.confirmMissingBillToRef();
            if (!proceed) {
              savingRef.current = false;
              if (mountedRef.current) setIsSaving(false);
              return { ok: false, type: 'aborted' };
            }
          }
        }
      }

      // Create-only soft gate: creating an order with no quote and/or nothing to
      // invoice is legal but usually a mistake, so prompt once with whichever
      // applies. Ordinary saves of an existing pro never reach this. The Quote #
      // half is unreachable for users the invoice-quote-number component pins to
      // Edit (LCs) — `quoteNumberRequired` already hard-errored above.
      if (createMode && options.confirmCreateWarnings) {
        const warnings = createOrderWarnings({
          quoteNumber:
            editedOrderFields.quoteNumber !== undefined
              ? editedOrderFields.quoteNumber
              : orderDetails.invoiceMain?.quoteNumber,
          lineItems: invoiceLineItemsRef.current ?? (orderDetails.invoiceLineItems ?? []),
          // `options` in this scope is the SAVE options, not the hook's — the
          // create spec is the unambiguous source of what is being created.
          isTemplate: createMode.spec.kind === 'template',
        });
        if (warnings.length > 0) {
          const proceed = await options.confirmCreateWarnings(warnings);
          if (!proceed) {
            savingRef.current = false;
            if (mountedRef.current) setIsSaving(false);
            return { ok: false, type: 'aborted' };
          }
        }
      }

      saveAbortRef.current?.abort();
      const controller = new AbortController();
      saveAbortRef.current = controller;

      /**
       * Re-fetch the order and reconcile the user's pending edits against what is now in
       * the DB. When another user moved something, drop ONLY the edits that collide, keep
       * the rest, badge what changed, and hand back a 'stale' result. Returns null when
       * the order is unchanged and the save may proceed.
       *
       * Called twice. Once BEFORE the write, as the pre-save check. And again when the
       * SERVER blocks the save on a leg conflict — which is this same situation caught a
       * few milliseconds later, in the window this check cannot cover (PRO 5068186: two
       * saves in the same second). Running the same reconciliation both times means the
       * user sees one behavior regardless of which layer caught it, and their pending
       * edits survive either way.
       *
       * `serverConflicts` folds in rows the backend flagged. The two field lists are
       * deliberately kept in step (orderLegDiff.ts CONFLICT_CHECKED_WIRE_FIELDS /
       * save_conflict.py CONFLICT_EXEMPT_LEG_FIELDS), but if they ever drift, the side
       * that actually refused the write is the one to believe.
       */
      const reconcileAgainstFresh = async (
        serverConflicts?: Array<{ legrecno?: number | null; field: string }>
      ): Promise<SaveResult | null> => {
        // Stale-data pre-check: re-fetch order details and compare against the
        // snapshot we last loaded. Same logic as OrderDetailsModalContent.tsx,
        // adapted for the hook (no pendingNewLegs / pendingRemovedLegs since
        // the hook doesn't manage structural leg edits — only existing legs).
        // Create mode skips the pre-check entirely: nothing is persisted yet and
        // the pronumber is the 'NEW'/0 sentinel, so the fetch would 404. A null
        // `data` makes the whole conflict block below inert — no legs to diff,
        // and the order-field compare is already guarded on `freshCheck.data`.
        const freshCheck = createMode
          ? ({ data: null as unknown as OrderDetails })
          : await ordersService.getOrderDetails(
              orderDetails.pronumber,
              controller.signal
            );
        if (controller.signal.aborted) return { ok: false, type: 'aborted' };

        const { changedOnSave, conflicts: newConflicts, drifted } =
          reconcileStagedEdits(freshCheck.data, orderDetails, {
            skipLegs: Boolean(createMode),
            serverConflicts,
          });

        if (drifted) {
          const freshLegs = freshCheck.data?.legs || [];
          const removedLegrecnos = Object.entries(changedOnSave)
            .filter(([, fields]) => fields.includes('__removed__'))
            .map(([legrecno]) => Number(legrecno));
          // legnum lookups for the caller's message. Removed legs are already gone from
          // freshLegs, so resolve those against the pre-refresh snapshot; changed and
          // added legs resolve against the fresh one.
          const legnumOf = (legrecno: number, legs: OrderLeg[]): number | undefined =>
            legs.find((l) => l.legrecno === legrecno)?.legnum;
          const removedLegnums = removedLegrecnos
            .map((lr) => legnumOf(lr, orderDetails.legs ?? []))
            .filter((n): n is number => n !== undefined)
            .sort((a, b) => a - b);
          const changedLegnums = Object.entries(changedOnSave)
            .filter(([, fields]) => !fields.includes('__removed__'))
            .map(([legrecno]) => legnumOf(Number(legrecno), freshLegs))
            .filter((n): n is number => n !== undefined)
            .sort((a, b) => a - b);

          // Update the canonical order data so the UI shows what's actually
          // in the DB. The caller's stale-handling code highlights which
          // fields changed; the data itself reflects the fresh server state.
          // (reconcileStagedEdits already dropped the colliding edits and set
          // the conflict rows.)
          if (mountedRef.current) {
            setOrderDetails(freshCheck.data);
          }
          // Note: the `finally` block resets isSaving — no need to clear it here.
          return {
            ok: false,
            type: 'stale',
            changedOnSave,
            freshOrderDetails: freshCheck.data,
            removedLegrecnos,
            removedLegnums,
            changedLegnums,
            conflicts: newConflicts,
          };
        }
        return null;     // nothing moved underneath us — the save may proceed
      };

      try {
        const staleResult = await reconcileAgainstFresh();
        if (staleResult) return staleResult;

        // Fetch live rather than trusting the tab's cached callouts. This also works
        // after switching tabs, and includes unsaved invoice lines. The backend
        // checks again against the persisted order/quote/lines before approval commits.
        if (!createMode && !String(orderDetails.pronumber).toUpperCase().startsWith('T') &&
            editedOrderFields.pendingApproval === 'RESOLVED') {
          const currentLines = invoiceLineItemsRef.current ?? orderDetails.invoiceLineItems ?? [];
          const estimate = await invoiceEstimateService.estimate(
            Number(orderDetails.pronumber),
            currentLines.map((line) => ({ ...line, recno: line.recno ?? undefined })),
          );
          const present = new Set(currentLines.map((line) => line.item.trim().toUpperCase()));
          // THIS DOCUMENT'S CHARGES ONLY — the same scope the server's gate uses
          // (invoice_reconciler/approval._lines_for_document with no invoicerecno).
          // `pendingApproval` is a column on `cms.invoice`, so the order carries one
          // flag per document and this save is resolving the MAIN invoice. A charge on
          // an accessorial answers to that accessorial's own flag.
          //
          // The one exception is the server's too: an accessorial the engine would have
          // to CREATE (`invoiceRecno == null`) has no flag of its own yet, so its charge
          // is the main invoice's to account for or it would be gated nowhere.
          //
          // Without this the client refused saves the server would have allowed — on an
          // accessorial-billing customer (Diageo pro 5138540) a chassis charge already
          // billed on accessorial #1 blocked the main invoice, naming an item the user
          // could not see on the screen they were standing on.
          const answersHere = (line: EstimatedLine): boolean =>
            isMainInvoiceLine(line) || line.invoiceRecno == null;
          const missing = [...new Set(estimate.lines
            .filter((line) => line.source === 'add' && answersHere(line) &&
              !(isMainInvoiceLine(line) && present.has(line.item.trim().toUpperCase())))
            .map((line) => line.item))];
          if (missing.length) {
            const message = 'Pending Approval cannot be RESOLVED until all suggested charges are reconciled. ' +
              'Add these charges to an invoice or record an Override reason: ' + missing.join(', ') + '.';
            if (mountedRef.current) setSaveError(message);
            return { ok: false, type: 'validation', errors: [message] };
          }
        }

        // Build the v1 save payload using the same helper the drawer uses.
        // currentLegs reflects what the user sees (originalLegs minus removed,
        // plus pendingNewLegs, with edits overlaid + sequential renumber).
        // We use `effectiveLegs` directly — buildLegSavePayload uses each leg's
        // `legnum` for the saved snapshot, and identifies new legs by their
        // negative `legrecno`, so the renumbered values are exactly right.
        const excluded = new Set(options.excludeLegrecnos ?? []);
        const removedLegObjects = orderDetails.legs.filter(l =>
          pendingRemovedLegrecnos.includes(l.legrecno) && !excluded.has(l.legrecno)
        );

        // Save-time auto-fill safety net: LegsGrid's onDriverAssigned hook
        // fills missing appt dates on the driver's scope at assignment time,
        // but anything the user does AFTER that (adding a leg, splitting via
        // the Drop toggle, changing legtype) can re-open holes that the hook
        // doesn't re-run for. Re-compute fills here against the final
        // effectiveLegs snapshot so the payload never ships with the gaps.
        const autoFills = computeAllScopeApptFills(effectiveLegs);
        const hasAutoFills = Object.keys(autoFills).length > 0;
        const editedLegsForPayload = (() => {
          if (!hasAutoFills && excluded.size === 0) return editedLegs;
          const out: typeof editedLegs = {};
          for (const [legrecnoStr, edits] of Object.entries(editedLegs)) {
            const lr = Number(legrecnoStr);
            if (excluded.has(lr)) continue;
            out[lr] = edits;
          }
          if (hasAutoFills) {
            for (const [legrecnoStr, fill] of Object.entries(autoFills)) {
              const lr = Number(legrecnoStr);
              if (excluded.has(lr)) continue;
              const staged = (out[lr] || {}) as Record<string, unknown>;
              // An explicitly staged blank is an INTENT, not a hole to backfill.
              //
              // The autofill runs over effectiveLegs, which already has the
              // staged edits overlaid, so a date the user just cleared looks
              // exactly like one that was never set — and spreading the fill
              // last put it straight back. That silently undid the delivery-appt
              // fan-out (planDownstreamApptClears) whenever a cleared leg shared
              // a scope with a driver: legs 1(Y) 2=delivery(Y) 3(Y,driver)
              // 4 5(Y,driver) clears leg 4, then refilled it from leg 3's kept
              // dates. bugfix/#33627.
              //
              // Only a blank the user STAGED is protected. A leg that was
              // already blank carries no key here, so the safety net this
              // autofill exists for — a leg added to a dispatched scope, a Drop
              // toggle splitting one — still fills exactly as before.
              const applicable = Object.fromEntries(
                Object.entries(fill).filter(([field]) => staged[field] !== ''),
              );
              out[lr] = { ...staged, ...applicable };
            }
          }
          return out;
        })();
        const baseCurrentLegs = excluded.size > 0
          ? effectiveLegs.filter(l => !excluded.has(l.legrecno))
          : effectiveLegs;
        const currentLegs = hasAutoFills
          ? baseCurrentLegs.map(l => {
              const fill = autoFills[l.legrecno];
              return fill ? { ...l, ...fill } : l;
            })
          : baseCurrentLegs;
        // Create mode: no server-side leg exists, so the "original" snapshot is
        // empty and nothing can be removed — every current leg emits as new.
        const legPayload = buildLegSavePayload({
          originalLegs: createMode ? [] : orderDetails.legs,
          currentLegs,
          editedLegs: editedLegsForPayload,
          removedLegs: createMode ? [] : removedLegObjects,
        });

        const orderData = buildOrderDataRows(editedOrderFields, orderDetails);
        // Invoice-TABLE field edits for the native engine's invoice section. (The
        // ordersall invoice-tab fields — cod, margin*, freightterms, etc. — ride
        // orderData above via ORDER_FIELD_COLUMNS, not here.)
        const invoiceData = buildInvoiceDataRows(editedOrderFields, orderDetails);
        // invli section: the full authoritative line-item set (native engine
        // soft-removes any pre-state line absent from it). Sent only when line
        // items actually changed. FE row fields -> invoice_lineitem DB columns.
        const toInvliRow = (r: {
          item: string; glCode: string; qty: number; rate: number;
          rateType: string; description: string; total: number; lineNumber: number;
        }) => ({
          liitem: r.item, limastype: r.glCode, liqty: r.qty, lirate: r.rate,
          liratetype: r.rateType, lidescription: r.description, litotal: r.total,
          linumber: r.lineNumber,
        });
        let invliData: Array<{ recno: number | null; new: any; old: any }> | undefined;
        if (createMode) {
          // Create: ALWAYS the full authoritative set (the backend inserts it
          // instead of copying the template's lines), every row recno-null —
          // including the seeded rows carrying buildCreateOrderSeed's recno: 0
          // "not a real row yet" sentinel. Falls back to the seeded lines when
          // the Invoicing tab never took the draft dirty.
          const createRows =
            invoiceDirty && invoiceLineItemsRef.current
              ? invoiceLineItemsRef.current
              : (orderDetails.invoiceLineItems ?? []);
          invliData = createRows.map((r) => ({
            recno: null,
            new: toInvliRow(r as any),
            old: {},
          }));
        } else if (invoiceDirty && invoiceLineItemsRef.current) {
          const oldByRecno = new Map(
            (orderDetails.invoiceLineItems ?? []).map((li) => [li.recno, li]),
          );
          invliData = invoiceLineItemsRef.current.map((r) => {
            const old = r.recno != null ? oldByRecno.get(r.recno) : undefined;
            return {
              recno: r.recno ?? null,
              new: toInvliRow(r as any),
              old: old ? toInvliRow(old as any) : {},
            };
          });
        }

        // --- Create branch: one atomic POST /orders, then hand the new pro back
        // to the host. Nothing else in this function runs (no side-channel — the
        // create endpoint is native-only and atomic; no local state flip — the
        // host owns the transition to the created order).
        if (createMode) {
          const spec = createMode.spec;
          // Same idempotency convention as saveOrderDetails: one nonce per save
          // action, reused on the dupe-bypass resend.
          const submitNonce =
            (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `create-${performance.now()}`;
          const stagedNotes = [
            ...(createMode.notes ?? []),
            ...(options.deadlineNote ? [options.deadlineNote] : []),
          ];
          // The create modal only SEEDS these fields; the order editor is
          // authoritative at save time. Send the user's current value — an edit
          // if present, else the modal-seeded value on the drawer — so a Move
          // Type / Bkg-BOL / Equipment # changed in the editor is what reaches
          // the backend, not the modal's original pick. (Same effective-value
          // shape as the client-side container-required check above.) These
          // ride in orderData too; the backend reconciles the two channels.
          const editorValue = (key: string) =>
            editedOrderFields[key] !== undefined
              ? editedOrderFields[key]
              : (orderDetails as any)?.[key];
          const basePayload = {
            movetype: editorValue('moveType') ?? spec.movetype,
            billto: spec.billto,
            customercode: spec.customercode,
            bol: editorValue('bookingNumber') ?? spec.bol,
            container: editorValue('container') ?? spec.container,
            ...(spec.templatepro ? { templatepro: spec.templatepro } : {}),
            submitNonce,
            ...legPayload,
            orderData,
            invoiceData,
            invliData: invliData ?? [],
            ...(stagedNotes.length > 0 ? { notes: stagedNotes } : {}),
          };
          // One create flow, two endpoints (#33471). A template's payload is the
          // same shape minus the order-only halves — a blueprint carries no
          // Bkg/BOL or container, and is never created FROM a template.
          const isTemplateCreate = spec.kind === 'template';
          const postCreate = (extra: { bypassDupeCheck: boolean }) =>
            isTemplateCreate
              ? templateService.createTemplate({
                  movetype: basePayload.movetype,
                  billto: basePayload.billto,
                  customercode: basePayload.customercode,
                  originateleg: legPayload.originateleg,
                  deliveryleg: legPayload.deliveryleg,
                  terminateleg: legPayload.terminateleg,
                  legs: legPayload.legs,
                  orderData,
                  invoiceData,
                  invliData: invliData ?? [],
                  ...(stagedNotes.length > 0 ? { notes: stagedNotes } : {}),
                  submitNonce,
                  ...extra,
                })
              : ordersService.createOrder({ ...basePayload, ...extra });

          let createResp: any = await postCreate({
            // The modal's "Save Order" bypass rides through to the save-time
            // re-check (V1 parity).
            bypassDupeCheck: spec.bypassDupeCheck,
          });

          // Same duplicate-confirm loop as the edit path, so the host's existing
          // dupe WarningConfirmModal works unchanged.
          if (createResp?.requiresConfirmation) {
            const dupes: Array<string | number> = createResp.dupes ?? [];
            const proceed = options.confirmDuplicate
              ? await options.confirmDuplicate(dupes)
              : false;
            if (!proceed) return { ok: false, type: 'duplicate', dupes };
            createResp = await postCreate({ bypassDupeCheck: true });
            if (createResp?.requiresConfirmation) {
              // Bypass didn't take (shouldn't happen) — don't loop.
              return { ok: false, type: 'duplicate', dupes: createResp.dupes ?? dupes };
            }
          }

          // A template allocates a "T…" string, an order a number — keep whichever
          // the server returned rather than coercing (Number('T82758') is NaN).
          const rawCreated = createResp?.pronumber;
          const createdPronumber: number | string = isTemplateCreate
            ? String(rawCreated)
            : Number(rawCreated);
          const createMessages: SaveUserMessage[] = Array.isArray(createResp?.userMessages)
            ? createResp.userMessages
            : [];
          // Fresh details for the created pro keep the host's post-save
          // consumers (override-approval recording, toasts) working unchanged.
          const created = await ordersService.getOrderDetails(
            createdPronumber,
            controller.signal,
          );
          if (controller.signal.aborted) return { ok: false, type: 'aborted' };
          // The order committed — drop the banner latch. The edit path's own
          // reset lives further down and this branch returns before reaching it.
          if (mountedRef.current) setValidationAttempted(false);
          return {
            ok: true,
            freshOrderDetails: created.data,
            userMessages: createMessages,
            createdPronumber,
          };
        }

        // Does the main save POST need to run? It carries the order + native
        // invoice/invli sections, so it fires whenever ANY of order/leg/invoice/
        // invli changed. (In cmsapi mode the invoice/invli sections are ignored by
        // cmsapi.pl and the side-channel below persists them; in native mode the
        // engine consumes them and reports invoiceHandled.)
        const orderOrLegChanged =
          orderData !== undefined ||
          Object.keys(editedLegs).length > 0 ||
          pendingNewLegs.length > 0 ||
          pendingRemovedLegrecnos.length > 0 ||
          invoiceData !== undefined ||
          invliData !== undefined;

        let resp: any = null;
        if (orderOrLegChanged) {
          // One stable nonce per save action — reused across the dupe-bypass retry so
          // the backend's (nonce, pro) idempotency key dedupes a double-submit. (native
          // mode only; cmsapi/shadow ignore it.)
          const submitNonce =
            (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `${orderDetails.pronumber}-${performance.now()}`;
          const basePayload = {
            pronumber: orderDetails.pronumber,
            ...legPayload,
            orderData,
            invoiceData,
            invliData,
            notes: options.deadlineNote,
            deletereason: options.deletereason,
            inputs: options.inputs,
            submitNonce,
            ...((adminOverrideActiveRef.current || adminOverrideUsedRef.current)
              ? { override: true } : {}),
          };
          resp = await ordersService.saveOrderDetails(basePayload);

          // Leg-conflict block: another user moved a leg between our pre-save check and
          // the write. NOTHING was written — so return before the invoice side-channels
          // and before the post-save reset, which would clear the very edits we are
          // trying to preserve. Reconciling here gives the user the same badges and the
          // same message as if our own pre-check had caught it, with their pending edits
          // intact; they review and hit Save again.
          if (resp?.conflict) {
            const conflictResult = await reconcileAgainstFresh(resp.conflicts ?? []);
            if (conflictResult) return conflictResult;
            // The refetch came back clean — the drift was undone, or the block was for
            // something the `conflicts` rows do not describe. Nothing was written either
            // way, so say so rather than falling through and reporting a save that never
            // happened.
            //
            // Prefer the server's own `userMessages` over `message` here. `message` is a
            // single fixed "another user changed the legs — review the highlighted legs"
            // string covering EVERY block reason, and on this branch there is nothing
            // highlighted, so on its own it tells the user to go look at something that
            // does not exist. `userMessages` carries the actual reason. (Seen 2026-09-04:
            // a leg blocked on the since-removed paid-leg guard reported "legs changed
            // while you were editing" with `conflicts: []`, while the real reason —
            // "Leg 1 already marked for Driver Pay as PAY by gwimove" — sat unread in
            // `userMessages`.)
            const conflictMessages: SaveUserMessage[] = Array.isArray(resp.userMessages)
              ? resp.userMessages
              : [];
            const reasons = conflictMessages
              .map((m) => m?.text)
              .filter((t): t is string => Boolean(t && t.trim()));
            return {
              ok: false,
              type: 'error',
              error: reasons.length
                ? reasons.join('\n')
                : resp.message ??
                  'This order changed elsewhere. Review and save again.',
            };
          }

          // Native-mode duplicate block: nothing was written. Ask the caller to
          // confirm; on "Save anyway" resend with bypassDupeCheck (same nonce).
          if (resp?.requiresConfirmation) {
            const dupes: Array<string | number> = resp.dupes ?? [];
            const proceed = options.confirmDuplicate
              ? await options.confirmDuplicate(dupes)
              : false;
            if (!proceed) return { ok: false, type: 'duplicate', dupes };
            resp = await ordersService.saveOrderDetails({
              ...basePayload,
              bypassDupeCheck: true,
            });
            if (resp?.requiresConfirmation) {
              // Bypass didn't take (shouldn't happen) — don't loop.
              return { ok: false, type: 'duplicate', dupes: resp.dupes ?? dupes };
            }
          }
        }

        // Invoice/invli side-channel persistence for cmsapi/shadow mode. In NATIVE
        // mode the engine already persisted the invoice + invli sections from the
        // payload (resp.invoiceHandled), so we skip these to avoid a double-write.
        // Only invoice-TABLE header fields go through /invoice-header now (the
        // ordersall invoice-tab fields rode orderData above); the invoice-approval
        // stamp (invoiceApprovedBy) also went through orderData so sr_approved fired.
        // Templates ("T…") route through the SAME side-channels — the endpoints are
        // template-aware and write templateinvoice / templateinvoice_lineitem.
        if (orderDetails.pronumber != null && !resp?.invoiceHandled) {
          const invoicePatch = buildInvoiceHeaderPatch(editedOrderFields);
          const resolvingApproval = invoicePatch?.pendingApproval === 'RESOLVED';
          if (invoicePatch && !resolvingApproval) {
            await ordersService.saveInvoiceHeader(orderDetails.pronumber, invoicePatch);
          }
          if (invoiceDirty && invoiceLineItemsRef.current) {
            await invoiceLineItemsService.saveBatch(
              orderDetails.pronumber,
              invoiceLineItemsRef.current,
              undefined,
              adminOverrideActiveRef.current || adminOverrideUsedRef.current,
              // Tell the batch that this same save sets Pending Approval, so its
              // re-flag stands down. The ordering below already protected the RESOLVED
              // case on this route; passing the value makes the rule the condition
              // rather than the sequence, and matches what native_save reads off its
              // own payload.
              invoicePatch?.pendingApproval,
            );
          }
          // The approval gate must see any lines added in this save. In cmsapi /
          // shadow these are separate requests, so resolve only after they succeed.
          if (invoicePatch && resolvingApproval) {
            await ordersService.saveInvoiceHeader(orderDetails.pronumber, invoicePatch);
          }
        }

        // Docs tab Invoice flags. A SIDE-CHANNEL write after the order save, for the
        // same reason the line-item batch is one: it then behaves identically whether
        // the order itself went through cmsapi, shadow or the native path, and a failed
        // order save writes nothing. V1 commits these with the order too (dosaverecord
        // `documentlist` -> processSaveDocumentsInvoicing); only the transport differs.
        //
        // Read from the ref, not the state variable — see docInvoiceFlagsRef.
        const stagedDocFlags = docInvoiceFlagsRef.current;
        if (orderDetails.pronumber != null && Object.keys(stagedDocFlags).length > 0) {
          await proDocumentsService.setInvoiceFlags(
            orderDetails.pronumber,
            Object.entries(stagedDocFlags).map(([mapRecno, forinvoice]) => ({
              map_recno: Number(mapRecno),
              forinvoice,
            })),
          );
          docInvoiceFlagsRef.current = {};
          setDocInvoiceFlags({});
        }

        const userMessages: SaveUserMessage[] = Array.isArray(resp?.userMessages)
          ? resp.userMessages
          : [];

        // Refresh
        const freshDetails = await ordersService.getOrderDetails(
          orderDetails.pronumber,
          controller.signal
        );
        if (controller.signal.aborted) return { ok: false, type: 'aborted' };

        if (mountedRef.current) {
          setOrderDetails(freshDetails.data);
          // The override edits are committed and tagged; the latch has done its job.
          adminOverrideUsedRef.current = false;
          setEditedOrderFields({});
          setEditedLegs({});
          setPendingNewLegs([]);
          setPendingRemovedLegrecnos([]);
          setSaveError(null);
          setValidationAttempted(false);
          // Invoice draft committed above — clear it so the refreshed details
          // re-seed the tab and hasChanges drops back to false.
          invoiceLineItemsRef.current = null;
          setInvoiceDirty(false);
          // Signal post-commit consumers (Invoicing tab Change-All fan-out).
          setSaveVersion((v) => v + 1);
          // Reaching here means the save fully landed, so nothing is left to flag. A
          // leg conflict returned above with the badges still on screen.
          setConflicts({});
        }

        return {
          ok: true,
          freshOrderDetails: freshDetails.data,
          userMessages,
        };
      } catch (error: any) {
        if (
          controller.signal.aborted ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return { ok: false, type: 'aborted' };
        }
        const detail = error.response?.data?.detail;
        let errorMessage = 'Failed to save changes. Please try again.';
        if (detail) {
          errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        if (mountedRef.current) setSaveError(errorMessage);
        return { ok: false, type: 'error', error: errorMessage };
      } finally {
        savingRef.current = false;
        if (mountedRef.current) setIsSaving(false);
      }
    },
    [
      orderDetails,
      editedOrderFields,
      editedLegs,
      pendingNewLegs,
      pendingRemovedLegrecnos,
      effectiveLegs,
      validateChanges,
      invoiceDirty,
    ]
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);
  const clearConflicts = useCallback(() => setConflicts({}), []);

  return {
    order,
    orderDetails,
    loading,
    loadFailed: loadError !== null,
    loadError,
    refresh,
    streetTurnOpportunities,
    orderViewers,

    // edits — real (Task P1.2)
    editedOrderFields,
    editedLegs,
    hasChanges,
    hasChangesIgnoringDispatchFlags,
    hasChangesIgnoringAutoStagedSend,
    updateOrderField,
    revertOrderField,
    snapshotEdits,
    countEditsSince,
    revertToSnapshot,
    updateLegField,
    resetEdits,
    setInvoiceLineItemsDraft,
    invoiceLineItemsDraft: invoiceLineItemsRef.current,
    setDocInvoiceFlagsDraft,
    docInvoiceFlagsDraft: docInvoiceFlags,
    saveVersion,
    _setEditedLegs: setEditedLegs,
    _setEditedOrderFields: setEditedOrderFields,
    _setOrderDetails: setOrderDetails,

    // structural leg edits — expose just the leg data; insertion intent is
    // an internal positioning concern for effectiveLegs composition.
    pendingNewLegs: pendingNewLegs.map(e => e.leg),
    pendingRemovedLegrecnos,
    effectiveLegs,
    originalLegs: orderDetails?.legs ?? [],
    quoteLookupContext,
    addLeg,
    removeLeg,
    restoreLeg,
    clearDriver,
    clearLegApptDates,
    dropApptClearPrompt,
    dismissDropApptClearPrompt,
    confirmDropApptClear,

    // save — real (Task P1.4)
    save,
    isSaving,
    saveError,
    clearSaveError,
    validateChanges,
    validationAttempted,
    setExtraSaveErrors,
    conflicts,
    clearConflicts,
    staleFields,
    clearStaleField,
  };
}
